import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card,
  Row,
  Col,
  Button,
  Tag,
  Space,
  Spin,
  Empty,
  Badge,
  Divider,
  Typography,
  Tooltip,
  Checkbox,
  Dropdown,
  Input,
  InputNumber,
  Modal,
  message,
  Drawer,
  List,
  Avatar,
  ConfigProvider,
  theme,
  AutoComplete,
} from 'antd';
import {
  ShoppingCartOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  FilterOutlined,
  ReloadOutlined,
  DownOutlined,
  PlusOutlined,
  MinusOutlined,
  SafetyCertificateOutlined,
  ThunderboltOutlined,
  HomeOutlined,
  BorderBottomOutlined,
  BorderLeftOutlined,
} from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import { useAuth } from '../contexts/AuthContext';
import { normalizeTheme, toAntdTheme } from '../utils/theme';
import { scopedKey, getActiveAccountId } from '../utils/accountSession';
import {
  parseComponentOptions,
  hasComponentChoices,
  resolveSlotSelection,
  computeOptionDifferentiators,
} from '../utils/ahriSystemSelection';
import { publishWebhookEventAuth } from '../services/webhookConsumerApi';
import useRealtimeSocket from '../hooks/useRealtimeSocket';

const { Title, Text } = Typography;

// Decode HTML entities
const decodeHtmlEntities = (text) => {
  if (!text || typeof text !== 'string') return text;
  const textarea = document.createElement('textarea');
  let decoded = text;
  let prev = '';
  let iterations = 0;
  while (decoded !== prev && iterations < 10) {
    prev = decoded;
    textarea.innerHTML = decoded;
    decoded = textarea.value;
    iterations++;
  }
  return decoded;
};

// Format number with commas
const formatNumber = (num) => {
  if (num === null || num === undefined) return '-';
  return Number(num).toLocaleString();
};

const resolveAssetUrl = (url) => {
  if (!url || typeof url !== 'string') return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/')) return `${API_BASE_URL}${url}`;
  return url;
};

const ImageWithTextFallback = ({ src, alt, size = 44, style = {} }) => {
  const [failed, setFailed] = useState(false);
  const resolvedSrc = String(src || '').trim();
  const showPlaceholder = failed || !resolvedSrc;
  if (showPlaceholder) {
    return (
      <div
        title={alt}
        style={{
          width: size,
          height: size,
          borderRadius: 4,
          background: '#f5f5f5',
          border: '1px solid #d9d9d9',
          color: '#8c8c8c',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          fontSize: 11,
          lineHeight: 1.1,
          padding: 4,
          ...style,
        }}
      >
        No image
      </div>
    );
  }
  return (
    <img
      src={resolvedSrc}
      alt={alt}
      style={{ width: size, height: size, objectFit: 'contain', borderRadius: 4, background: '#f5f5f5', ...style }}
      onError={() => setFailed(true)}
    />
  );
};

// No built-in filters: every System Shop facet (brand, cooling capacity,
// SEER2/HSPF2/EER2, etc.) must be configured on the admin's AHRI Data >
// System Shop Filters screen (system_shop_filters). An account with
// nothing saved there shows zero filters until an admin configures them --
// no field name is assumed/seeded here.
const DEFAULT_SYSTEM_SHOP_FILTERS = [];

const DEFAULT_SYSTEM_SHOP_SEARCH = {
  enabled: true,
  placeholder: 'Search by model number, brand, or AHRI ID',
  query_fields: ['search_text^1'],
  phrase_fields: [],
  min_match: '2<75%',
  operator: 'AND',
  tie: 0.1,
  qs: 1,
  ps: 2,
  bq: [],
  bf: [],
  suggest_fields: [
    'rows_referenceid',
    'rows_outdoormodelnumber',
    'rows_indoormodelnumber',
    'rows_outdoorbrandname',
    'rows_indoorbrandname',
  ],
  suggest_limit: 8,
};
const SYSTEM_SHOP_MATCH_TYPES = ['complete', 'partial', 'none'];

const normalizeSystemShopMatchTypes = (value, { includeUnmatched = false } = {}) => {
  const rawValues = Array.isArray(value)
    ? value
    : typeof value === 'string'
    ? value.split(/[\s,|]+/)
    : [];
  const aliases = {
    complete_match: 'complete',
    'complete-match': 'complete',
    partial_match: 'partial',
    'partial-match': 'partial',
    unmatched: 'none',
    no_match: 'none',
    'no-match': 'none',
  };
  const selected = rawValues
    .map((entry) => aliases[String(entry || '').trim()] || aliases[String(entry || '').trim().toLowerCase()] || String(entry || '').trim().toLowerCase())
    .filter((entry, index, list) => SYSTEM_SHOP_MATCH_TYPES.includes(entry) && list.indexOf(entry) === index);
  if (selected.length) {
    return SYSTEM_SHOP_MATCH_TYPES.filter((entry) => selected.includes(entry));
  }
  return includeUnmatched ? [...SYSTEM_SHOP_MATCH_TYPES] : ['complete', 'partial'];
};

const normalizeSolrRowField = (field) => {
  const raw = String(field || '').trim();
  if (!raw) return '';
  const lower = raw.toLowerCase();
  const hasRows = lower.startsWith('rows_');
  const hasProdAttr = lower.startsWith('prodattr_');
  if (hasRows || hasProdAttr) {
    const prefix = hasRows ? 'rows_' : 'prodattr_';
    const rest = raw.slice(prefix.length);
    const cleanedRest = rest
      .split('')
      .map((ch) => (/[a-zA-Z0-9]/.test(ch) ? ch : '_'))
      .join('')
      .replace(/_+/g, '_')
      .replace(/^_+|_+$/g, '');
    return cleanedRest ? `${prefix}${cleanedRest.toLowerCase()}` : '';
  }
  const cleaned = raw
    .split('')
    .map((ch) => (/[a-zA-Z0-9]/.test(ch) ? ch : '_'))
    .join('')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
  return cleaned ? `rows_${cleaned.toLowerCase()}` : '';
};

// Parses size-like strings such as "1/2 inch", "1-1/2\"", "2 1/2 in", "3 inch"
// into a plain decimal for numeric comparison -- handles whole numbers,
// simple fractions, and mixed numbers, with any unit suffix ignored. Returns
// NaN for anything that doesn't start with a recognizable number/fraction.
const parseFractionValue = (value) => {
  const text = String(value ?? '').trim().toLowerCase();
  if (!text) return NaN;
  const match = text.match(
    /^(\d+(?:\.\d+)?)?\s*[-\s]\s*(\d+)\s*\/\s*(\d+)|^(\d+)\s*\/\s*(\d+)|^(\d+(?:\.\d+)?)/
  );
  if (!match) return NaN;
  if (match[2] !== undefined && match[3] !== undefined) {
    const whole = match[1] ? Number(match[1]) : 0;
    const denominator = Number(match[3]);
    return denominator ? whole + Number(match[2]) / denominator : whole;
  }
  if (match[4] !== undefined && match[5] !== undefined) {
    const denominator = Number(match[5]);
    return denominator ? Number(match[4]) / denominator : NaN;
  }
  if (match[6] !== undefined) return Number(match[6]);
  return NaN;
};

const normalizeSystemShopFilters = (value) => {
  if (!Array.isArray(value)) {
    return DEFAULT_SYSTEM_SHOP_FILTERS.map((entry) => ({ ...entry, fields: [...entry.fields] }));
  }
  const normalized = value
    .filter((entry) => entry && typeof entry === 'object')
    .map((entry) => {
      const key = String(entry.key || entry.id || entry.name || '').trim();
      const label = String(entry.label || entry.title || '').trim() || key;
      const rawFields = entry.fields || entry.attributes || entry.attribute_fields || [];
      const fields = Array.isArray(rawFields)
        ? rawFields.map((field) => normalizeSolrRowField(field)).filter(Boolean)
        : String(rawFields || '')
            .split(/[\n,]+/)
            .map((field) => normalizeSolrRowField(field))
            .filter(Boolean);
      const valueType = String(entry.value_type || entry.type || 'string').toLowerCase();
      const sortOrder = String(entry.sort_order || entry.sort || 'asc').toLowerCase();
      const rawDisplayMap = entry.value_display_map || entry.display_map || entry.value_map || {};
      let valueDisplayMap = {};
      if (typeof rawDisplayMap === 'string') {
        try {
          valueDisplayMap = JSON.parse(rawDisplayMap);
        } catch {
          valueDisplayMap = {};
        }
      } else if (rawDisplayMap && typeof rawDisplayMap === 'object') {
        valueDisplayMap = rawDisplayMap;
      }
      return {
        key,
        label,
        fields,
        value_type: ['number', 'fraction'].includes(valueType) ? valueType : 'string',
        sort_order: sortOrder === 'desc' ? 'desc' : 'asc',
        value_display_map: valueDisplayMap || {},
      };
    })
    .filter((entry) => entry.key);
  return normalized;
};

const parseAdvancedFilterValues = (raw) => {
  const text = String(raw || '').trim();
  if (!text) return [];
  return text
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const idx = line.indexOf('=');
      if (idx === -1) {
        return { label: line, value: line, query: '' };
      }
      const label = line.slice(0, idx).trim();
      const query = line.slice(idx + 1).trim();
      return { label: label || query, value: label || query, query };
    })
    .filter((entry) => entry.label);
};

const normalizeSystemShopAdvancedFilters = (value) => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .filter((entry) => entry && typeof entry === 'object')
    .map((entry) => {
      const key = String(entry.key || entry.id || entry.name || '').trim();
      const label = String(entry.label || entry.title || '').trim() || key;
      const rawValues = entry.values || entry.values_raw || [];
      const values = Array.isArray(rawValues)
        ? rawValues
            .filter((item) => item && typeof item === 'object')
            .map((item) => ({
              label: String(item.label || item.value || '').trim(),
              value: String(item.value || item.label || '').trim(),
              query: String(item.query || '').trim(),
            }))
            .filter((item) => item.label)
        : parseAdvancedFilterValues(rawValues);
      return {
        key: key || label,
        label,
        values,
        values_raw: typeof entry.values_raw === 'string' ? entry.values_raw : '',
      };
    })
    .filter((entry) => entry.key);
};

const normalizeSystemShopSearch = (value) => {
  if (!value || typeof value !== 'object') {
    return { ...DEFAULT_SYSTEM_SHOP_SEARCH, query_fields: [...DEFAULT_SYSTEM_SHOP_SEARCH.query_fields] };
  }
  const listify = (raw, fallback) => {
    if (Array.isArray(raw)) {
      return raw.map((item) => String(item).trim()).filter(Boolean);
    }
    if (typeof raw === 'string') {
      return raw
        .split(/[\n,]+/)
        .map((item) => item.trim())
        .filter(Boolean);
    }
    return fallback;
  };
  const enabled =
    typeof value.enabled === 'boolean'
      ? value.enabled
      : typeof value.enabled === 'string'
      ? value.enabled.trim().toLowerCase() === 'true'
      : DEFAULT_SYSTEM_SHOP_SEARCH.enabled;
  const operator = String(value.operator || DEFAULT_SYSTEM_SHOP_SEARCH.operator).toUpperCase();
  return {
    enabled,
    placeholder: String(value.placeholder || DEFAULT_SYSTEM_SHOP_SEARCH.placeholder),
    query_fields: listify(value.query_fields, [...DEFAULT_SYSTEM_SHOP_SEARCH.query_fields]),
    phrase_fields: listify(value.phrase_fields, []),
    min_match: String(value.min_match || DEFAULT_SYSTEM_SHOP_SEARCH.min_match),
    operator: operator === 'OR' ? 'OR' : 'AND',
    tie: Number.isFinite(Number(value.tie)) ? Number(value.tie) : DEFAULT_SYSTEM_SHOP_SEARCH.tie,
    qs: Number.isFinite(Number(value.qs)) ? Number(value.qs) : DEFAULT_SYSTEM_SHOP_SEARCH.qs,
    ps: Number.isFinite(Number(value.ps)) ? Number(value.ps) : DEFAULT_SYSTEM_SHOP_SEARCH.ps,
    bq: listify(value.bq, []),
    bf: listify(value.bf, []),
    suggest_fields: listify(value.suggest_fields, [...DEFAULT_SYSTEM_SHOP_SEARCH.suggest_fields]),
    suggest_limit: Number.isFinite(Number(value.suggest_limit))
      ? Math.max(1, Number(value.suggest_limit))
      : DEFAULT_SYSTEM_SHOP_SEARCH.suggest_limit,
  };
};

const buildEmptyFilterState = (filters) =>
  filters.reduce((acc, filter) => {
    acc[filter.key] = [];
    return acc;
  }, {});

const mergeFilterState = (prev, filters) => {
  const next = buildEmptyFilterState(filters);
  filters.forEach((filter) => {
    if (prev && Array.isArray(prev[filter.key])) {
      next[filter.key] = prev[filter.key];
    }
  });
  return next;
};

// Helper to extract values with counts from facet array
const extractFacetItems = (facetArray) => {
  if (!Array.isArray(facetArray)) return [];
  return facetArray
    .map((f) =>
      typeof f === 'object' && f !== null
        ? { value: f.value, count: typeof f.count === 'number' ? f.count : null }
        : { value: f, count: null }
    )
    .filter((item) => item.value !== undefined && item.value !== null && item.value !== '');
};

// Helper to extract values with counts from product attribute facet object
const extractProductAttrFacetItems = (facetObj) => {
  if (!facetObj) return [];
  if (Array.isArray(facetObj.values)) {
    return facetObj.values
      .map((v) =>
        typeof v === 'object' && v !== null
          ? { value: v.value, count: typeof v.count === 'number' ? v.count : null }
          : { value: v, count: null }
      )
      .filter((item) => item.value !== undefined && item.value !== null && item.value !== '');
  }
  if (Array.isArray(facetObj)) {
    return facetObj
      .map((v) =>
        typeof v === 'object' && v !== null
          ? { value: v.value, count: typeof v.count === 'number' ? v.count : null }
          : { value: v, count: null }
      )
      .filter((item) => item.value !== undefined && item.value !== null && item.value !== '');
  }
  if (typeof facetObj === 'string' || typeof facetObj === 'number') {
    return [{ value: facetObj, count: null }];
  }
  return [];
};

const AhriSystemShop = () => {
  const { getAuthHeaders, isAuthenticated, loading: authLoading, account } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const lastClickPublishRef = React.useRef(0);
  const lastRealtimeToastRef = React.useRef(0);
  const [lastRealtimeEvent, setLastRealtimeEvent] = useState(null);

  const publishUiClickEvent = useCallback((event) => {
    if (!isAuthenticated) return;
    const now = Date.now();
    // Avoid flooding from accidental double-click storms.
    if (now - lastClickPublishRef.current < 120) return;
    lastClickPublishRef.current = now;
    const target = event?.target;
    if (!target || typeof target !== 'object') return;
    const tagName = String(target.tagName || '').toLowerCase();
    const id = String(target.id || '');
    const className = String(target.className || '')
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 4)
      .join(' ');
    const text = String(target.innerText || target.textContent || '')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 160);
    const closestContextNode = typeof target.closest === 'function'
      ? target.closest('[data-event-context], [data-ahri-id], [data-product-code]')
      : null;
    const dataset = closestContextNode?.dataset || {};
    void publishWebhookEventAuth(
      {
        event_type: 'system_shop.ui_click',
        source: 'system_shop',
        context: {
          page: 'ahri_system_shop',
          path: location.pathname,
          account_id: account?.id || null,
        },
        data: {
          tag: tagName,
          id,
          className,
          text,
          eventContext: dataset.eventContext || '',
          ahriId: dataset.ahriId || '',
          productCode: dataset.productCode || '',
          productType: dataset.productType || '',
          outdoorProductCode: dataset.outdoorProduct || '',
          indoorProductCode: dataset.indoorProduct || '',
          furnaceProductCode: dataset.furnaceProduct || '',
          programName: dataset.programName || '',
          x: Number(event?.clientX || 0),
          y: Number(event?.clientY || 0),
        },
      },
      getAuthHeaders()
    ).catch((err) => {
      // Keep UX non-blocking; just log for troubleshooting.
      // eslint-disable-next-line no-console
      console.warn('system_shop.ui_click publish failed', err);
    });
  }, [account?.id, getAuthHeaders, isAuthenticated, location.pathname]);

  useEffect(() => {
    const handler = (event) => publishUiClickEvent(event);
    document.addEventListener('click', handler, true);
    return () => {
      document.removeEventListener('click', handler, true);
    };
  }, [publishUiClickEvent]);

  // Opens the AHRI reference id on ahridirectory.org's public "details" page. The
  // details URL is keyed by AHRI's internal ProgramId, which isn't derivable
  // client-side, so this calls our backend proxy (GET .../systems/{id}/directory-url)
  // to resolve it. The tab is opened synchronously (before the fetch) and only
  // navigated once the URL resolves, since most browsers block a window.open()
  // call made after an intervening await inside a click handler.
  const openAhriDirectoryLink = useCallback(
    (ahriId, event) => {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      const normalizedId = String(ahriId || '').trim();
      if (!normalizedId) return;
      const newTab = window.open('', '_blank', 'noopener,noreferrer');
      (async () => {
        try {
          const resp = await fetch(
            `${API_BASE_URL}/api/v1/ahri/systems/${encodeURIComponent(normalizedId)}/directory-url`,
            { headers: getAuthHeaders() }
          );
          const data = await resp.json().catch(() => ({}));
          if (!resp.ok || !data?.url) {
            if (newTab && !newTab.closed) newTab.close();
            return;
          }
          if (newTab && !newTab.closed) {
            newTab.location.href = data.url;
          } else {
            window.open(data.url, '_blank', 'noopener,noreferrer');
          }
        } catch (err) {
          if (newTab && !newTab.closed) newTab.close();
          // eslint-disable-next-line no-console
          console.warn('AHRI Directory link error:', err);
        }
      })();
    },
    [getAuthHeaders]
  );

  // Renders "12345" as a clickable id that opens the AHRI Directory details page
  // for it in a new tab (see openAhriDirectoryLink above). Falls back to plain
  // "-" text when there's no id to link.
  const renderAhriIdText = useCallback(
    (ahriId) => {
      const idText = ahriId || '-';
      if (!ahriId) return idText;
      return (
        <span
          role="button"
          tabIndex={0}
          onClick={(event) => openAhriDirectoryLink(ahriId, event)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              openAhriDirectoryLink(ahriId, event);
            }
          }}
          title="Open on AHRI Directory"
          style={{ cursor: 'pointer', textDecoration: 'underline dotted', textUnderlineOffset: 2 }}
        >
          {idText}
        </span>
      );
    },
    [openAhriDirectoryLink]
  );

  const handleRealtimeEvent = useCallback((eventName, payload) => {
    const now = Date.now();
    const summary =
      payload?.message ||
      payload?.detail ||
      payload?.status ||
      (typeof payload === 'string' ? payload : '');
    setLastRealtimeEvent({
      eventName,
      summary: String(summary || '').slice(0, 180),
      receivedAt: now,
    });
    if (now - lastRealtimeToastRef.current < 4000) {
      return;
    }
    lastRealtimeToastRef.current = now;
    message.info({
      content: `Realtime: ${eventName}${summary ? ` - ${String(summary).slice(0, 80)}` : ''}`,
      duration: 2,
    });
  }, []);

  const {
    status: websocketStatus,
    socketId: websocketSocketId,
    lastError: websocketError,
    isConnected: websocketConnected,
  } = useRealtimeSocket({
    enabled: isAuthenticated && !authLoading,
    room: 'ahri_system_shop',
    events: useMemo(() => ['ahri.system_shop.update'], []),
    clientName: 'ahri_system_shop',
    onEvent: handleRealtimeEvent,
  });

  const renderImage = useCallback((src, alt, size = 44, style = {}) => (
    <ImageWithTextFallback src={resolveAssetUrl(src)} alt={alt} size={size} style={style} />
  ), []);

  const [systemShopFilters, setSystemShopFilters] = useState(DEFAULT_SYSTEM_SHOP_FILTERS);
  const [systemShopConfigLoading, setSystemShopConfigLoading] = useState(false);
  const [systemShopSearch, setSystemShopSearch] = useState(DEFAULT_SYSTEM_SHOP_SEARCH);
  const [systemShopTheme, setSystemShopTheme] = useState(normalizeTheme(null));
  const [systemShopPreviewTheme, setSystemShopPreviewTheme] = useState(null);
  const antdTheme = useMemo(
    () => toAntdTheme(systemShopPreviewTheme || systemShopTheme),
    [systemShopPreviewTheme, systemShopTheme]
  );
  const [systemShopAdvancedFilters, setSystemShopAdvancedFilters] = useState([]);
  const [selectedAdvancedFilters, setSelectedAdvancedFilters] = useState({});
  const [systemShopFilterOrder, setSystemShopFilterOrder] = useState([]);
  const [systemShopMatchTypes, setSystemShopMatchTypes] = useState(['complete', 'partial']);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtersCollapsed, setFiltersCollapsed] = useState(
    typeof window === 'undefined' ? false : window.innerWidth < 992
  );
  const [filterLayout, setFilterLayout] = useState('top'); // top | left
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const searchTimerRef = React.useRef(null);
  const filterRefreshTimerRef = React.useRef(null);
  const isBootstrappingRef = React.useRef(false);
  const lastFacetRequestRef = React.useRef({ key: '', ts: 0 });
  const [, setOpenFacetKey] = useState(null);
  const [advancedFilterCounts, setAdvancedFilterCounts] = useState({});
  const [advancedCountsLoading, setAdvancedCountsLoading] = useState({});
  const lastFilterSnapshotRef = React.useRef({ standard: '', advanced: '' });
  const lastAdvancedCountsSnapshotRef = React.useRef('');
  const lastAdvancedCountsStandardSnapshotRef = React.useRef('');

  // Facet state
  const [facetData, setFacetData] = useState(buildEmptyFilterState(DEFAULT_SYSTEM_SHOP_FILTERS));
  const [facetsLoading, setFacetsLoading] = useState(false);

  // Selected filters
  const [selectedFilters, setSelectedFilters] = useState(buildEmptyFilterState(DEFAULT_SYSTEM_SHOP_FILTERS));

  // Results
  const [results, setResults] = useState([]);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [totalFound, setTotalFound] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);

  // Cart
  const [cart, setCart] = useState([]);
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false);

  // Product detail modal
  const [selectedSystem, setSelectedSystem] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [productDrawerOpen, setProductDrawerOpen] = useState(false);
  const [productDrawerWidth, setProductDrawerWidth] = useState(() =>
    typeof window === 'undefined' ? 720 : Math.round(window.innerWidth * 0.5)
  );
  const [productDrawerResizing, setProductDrawerResizing] = useState(false);
  const productDrawerResizeRef = React.useRef({ startX: 0, startWidth: 720 });
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [expandedProductCodes, setExpandedProductCodes] = useState({});

  // Compare systems
  const [compareSelections, setCompareSelections] = useState([]);
  
  // Product details cache
  const [productDetails, setProductDetails] = useState({});

  const sortFacetItems = (items, sortOrder, valueType) => {
    const map = new Map();
    items.forEach((item) => {
      if (item.value === null || item.value === undefined || item.value === '') return;
      const key = String(item.value);
      const prev = map.get(key);
      const nextCount = typeof item.count === 'number' ? item.count : prev?.count ?? null;
      map.set(key, { value: item.value, count: nextCount });
    });
    const deduped = Array.from(map.values());
    if (valueType === 'number' || valueType === 'fraction') {
      // parseFractionValue also parses plain decimals identically to Number()
      // (and additionally parses "1/2"/"1-1/2"-style fractions), so a "number"
      // field with a mix of decimal and fraction values still sorts by actual
      // size instead of a fraction string parsing as NaN.
      return deduped.sort((a, b) => {
        const aNum = parseFractionValue(a.value);
        const bNum = parseFractionValue(b.value);
        const aValid = Number.isFinite(aNum);
        const bValid = Number.isFinite(bNum);
        if (!aValid && !bValid) return 0;
        if (!aValid) return 1;
        if (!bValid) return -1;
        return sortOrder === 'desc' ? bNum - aNum : aNum - bNum;
      });
    }
    return deduped.sort((a, b) =>
      sortOrder === 'desc'
        ? String(b.value).localeCompare(String(a.value), undefined, { numeric: true, sensitivity: 'base' })
        : String(a.value).localeCompare(String(b.value), undefined, { numeric: true, sensitivity: 'base' })
    );
  };

  // Helper to process facets from API response
  const processFacets = (facets, productAttrFacets, filterConfig) => {
    const output = {};
    filterConfig.forEach((filter) => {
      const items = [];
      (filter.fields || []).forEach((field) => {
        if (!field) return;
        if (field.startsWith('prodattr_')) {
          items.push(...extractProductAttrFacetItems(productAttrFacets[field]));
        } else {
          items.push(...extractFacetItems(facets[field]));
        }
      });
      output[filter.key] = sortFacetItems(
        items,
        filter.sort_order || 'asc',
        filter.value_type || 'string'
      );
    });
    return output;
  };

  const buildOrClause = (field, values, isNumeric = false) => {
    if (!Array.isArray(values) || values.length === 0) return null;
    const clauses = values.map((val) => {
      if (isNumeric) return `${field}:${val}`;
      const safe = String(val).replace(/"/g, '\\"');
      return `${field}:"${safe}"`;
    });
    return `(${clauses.join(' OR ')})`;
  };

  const buildFieldGroupClause = (fields, values, isNumeric = false) => {
    if (!Array.isArray(fields) || fields.length === 0) return null;
    const clauses = fields
      .map((field) => buildOrClause(field, values, isNumeric))
      .filter(Boolean);
    if (!clauses.length) return null;
    return clauses.length === 1 ? clauses[0] : `(${clauses.join(' OR ')})`;
  };

  const buildSearchFilters = useCallback(
    (filtersState, advancedOverride = null) => {
      const apiFilters = {};
      systemShopFilters.forEach((filter) => {
        const values = filtersState?.[filter.key] || [];
        if (!Array.isArray(values) || values.length === 0) return;
        const clause = buildFieldGroupClause(
          filter.fields || [],
          values,
          filter.value_type === 'number'
        );
        if (!clause) return;
        apiFilters._raw_fq = apiFilters._raw_fq || [];
        apiFilters._raw_fq.push(clause);
      });
      const advancedSelections =
        advancedOverride && typeof advancedOverride === 'object' ? advancedOverride : selectedAdvancedFilters;
      systemShopAdvancedFilters.forEach((filter) => {
        const selected = advancedSelections?.[filter.key] || [];
        if (!Array.isArray(selected) || selected.length === 0) return;
        const clauses = selected
          .map((value) => filter.values.find((entry) => entry.value === value || entry.label === value))
          .map((entry) => entry?.query)
          .filter(Boolean);
        if (!clauses.length) return;
        const clause = clauses.length > 1 ? `(${clauses.join(' OR ')})` : clauses[0];
        apiFilters._raw_fq = apiFilters._raw_fq || [];
        apiFilters._raw_fq.push(clause);
      });
      return apiFilters;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [systemShopFilters, systemShopAdvancedFilters, selectedAdvancedFilters]
  );

  const extractSuggestionValues = (docs, fields) => {
    const values = [];
    docs.forEach((doc) => {
      fields.forEach((field) => {
        const raw = doc[field];
        if (raw === null || raw === undefined) return;
        if (Array.isArray(raw)) {
          raw.forEach((item) => values.push(String(item)));
        } else {
          values.push(String(raw));
        }
      });
    });
    return values;
  };

  const fetchSuggestions = useCallback(
    async (term) => {
      const cleaned = (term || '').trim();
      if (!systemShopSearch?.enabled || cleaned.length < 2) {
        setSearchSuggestions([]);
        return;
      }
      const headers = getAuthHeaders();
      if (!headers['X-API-Key'] && !headers['Authorization']) {
        return;
      }
      setSearching(true);
      try {
        const filters = buildSearchFilters(selectedFilters);
        const payload = {
          query: cleaned,
          page: 1,
          page_size: systemShopSearch.suggest_limit || 8,
          filters,
          include_product_attr_facets: false,
          context: 'system_shop',
          search_config: systemShopSearch,
        };
        if (systemShopSearch?.query_type) {
          payload.query_type = systemShopSearch.query_type;
        }
        payload.match_types = systemShopMatchTypes;
        const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/solr/search`, {
          method: 'POST',
          headers,
          body: JSON.stringify(payload),
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          return;
        }
        const docs = data.docs || [];
        const suggestFields = systemShopSearch.suggest_fields || [];
        const rawValues = extractSuggestionValues(docs, suggestFields);
        const unique = [...new Set(rawValues.map((value) => value.trim()).filter(Boolean))].slice(
          0,
          systemShopSearch.suggest_limit || 8
        );
        setSearchSuggestions(
          unique.map((value) => ({
            value,
            label: decodeHtmlEntities(value),
          }))
        );
      } catch (err) {
        console.warn('Failed to fetch suggestions:', err);
      } finally {
        setSearching(false);
      }
    },
    [getAuthHeaders, selectedFilters, systemShopSearch, buildSearchFilters, systemShopMatchTypes]
  );

  const getDisplayValue = (filter, value) => {
    const map = filter?.value_display_map || {};
    if (value === null || value === undefined) return '';
    const key = String(value);
    return map[key] || key;
  };

  const renderFacetCheckboxGroup = (options, selectedValues, onChange, ariaLabel, filter) => {
    if (!options || options.length === 0) {
      return (
        <Text type="secondary" style={{ fontSize: 12 }}>
          No values available
        </Text>
      );
    }

    return (
      <Checkbox.Group value={selectedValues} onChange={onChange} style={{ width: '100%' }}>
        <div
          role="group"
          aria-label={ariaLabel}
          style={{
            maxHeight: 200,
              overflowY: 'scroll',
              border: 'none',
              borderRadius: 6,
            padding: 8,
            background: '#ffffff',
              scrollbarGutter: 'stable',
          }}
        >
          {options.map((option) => {
            const labelText = decodeHtmlEntities(getDisplayValue(filter, option.value));
            const isZeroCount = typeof option.count === 'number' && option.count === 0;
            return (
              <div key={String(option.value)} style={{ marginBottom: 6 }}>
                <Checkbox value={option.value} disabled={isZeroCount} className="d-flex align-items-center">
                  <span className="flex-grow-1 text-truncate" style={{ fontSize: 13 }}>
                    {labelText}
                  </span>
                  {typeof option.count === 'number' && (
                    <span className="text-muted small ms-2">{option.count.toLocaleString()}</span>
                  )}
                </Checkbox>
              </div>
            );
          })}
        </div>
      </Checkbox.Group>
    );
  };

  const renderFacetDropdown = (label, options, selectedValues, onChange, filter, facetKey, isLoading) => {
    const count = Array.isArray(selectedValues) ? selectedValues.length : 0;
    const optionCountMap = new Map((options || []).map((item) => [String(item.value), item.count]));
    const selectedText =
      Array.isArray(selectedValues) && selectedValues.length
        ? selectedValues
            .map((value) => {
              const display = decodeHtmlEntities(getDisplayValue(filter, value));
              const itemCount = optionCountMap.get(String(value));
              return typeof itemCount === 'number' ? `${display} (${itemCount.toLocaleString()})` : display;
            })
            .join(', ')
        : `Select ${label}`;
    return (
      <Dropdown
        trigger={['click']}
        placement="bottomLeft"
        onOpenChange={(open) => {
          if (open) {
            setOpenFacetKey(facetKey || label);
            if (String(facetKey || '').startsWith('advanced:')) {
              const advKey = String(facetKey || '').replace('advanced:', '');
              const advFilter = systemShopAdvancedFilters.find((f) => f.key === advKey);
              if (advFilter) {
                loadAdvancedFilterCounts(advFilter, selectedAdvancedFilters);
              }
            }
          }
        }}
        popupRender={() => (
          <div
            style={{
              minWidth: 240,
              maxWidth: 320,
              padding: 12,
              background: '#ffffff',
              borderRadius: 12,
              border: '1px solid #e2e8f0',
              boxShadow: '0 12px 24px rgba(15, 23, 42, 0.12)',
            }}
          >
            {isLoading && (
              <div className="d-flex align-items-center gap-2 text-muted small mb-2">
                <Spin size="small" />
                Loading values...
              </div>
            )}
            {renderFacetCheckboxGroup(options, selectedValues, onChange, `Select ${label}`, filter)}
            <div className="d-flex align-items-center justify-content-between mt-2">
              <Button
                size="small"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onChange([]);
                }}
                disabled={!count}
              >
                Clear
              </Button>
              <span className="text-muted small">{count ? `${count} selected` : 'No selection'}</span>
            </div>
          </div>
        )}
      >
        <Button className="w-100 d-flex align-items-center justify-content-between">
          <span className="text-truncate">{selectedText}</span>
          <span className="d-flex align-items-center gap-2">
            {count > 0 && (
              <Tag color="blue" className="mb-0">
                {count}
              </Tag>
            )}
            <DownOutlined />
          </span>
        </Button>
      </Dropdown>
    );
  };

  const renderAdvancedDropdown = (filter) => {
    const countMap = advancedFilterCounts[filter.key] || {};
    const options = (filter.values || [])
      .map((entry) => {
        const value = entry.value || entry.label;
        return {
          value,
          count: typeof countMap[value] === 'number' ? countMap[value] : null,
        };
      })
      .filter((entry) => entry.value);
    const selected = selectedAdvancedFilters[filter.key] || [];
    return renderFacetDropdown(filter.label || filter.key, options, selected, (vals) => {
      const normalized = Array.isArray(vals)
        ? (vals.length ? [vals[vals.length - 1]] : [])
        : [];
      const next = {
        ...selectedAdvancedFilters,
        [filter.key]: normalized,
      };
      setSelectedAdvancedFilters(next);
      loadAdvancedFilterCounts(filter, next);
    }, { value_display_map: {} }, `advanced:${filter.key}`, advancedCountsLoading[filter.key]);
  };

  const orderedFilters = useMemo(() => {
    const standard = systemShopFilters.map((entry) => ({
      type: 'standard',
      key: entry.key,
      label: entry.label,
      entry,
    }));
    const advanced = systemShopAdvancedFilters.map((entry) => ({
      type: 'advanced',
      key: entry.key,
      label: entry.label,
      entry,
    }));
    const byKey = new Map([...standard, ...advanced].map((item) => [`${item.type}:${item.key}`, item]));
    const ordered = [];
    (systemShopFilterOrder || []).forEach((item) => {
      const key = `${item.type}:${item.key}`;
      if (byKey.has(key)) {
        ordered.push(byKey.get(key));
        byKey.delete(key);
      }
    });
    return [...ordered, ...Array.from(byKey.values())];
  }, [systemShopFilters, systemShopAdvancedFilters, systemShopFilterOrder]);

  // Fetch facets - can be called with or without current filters
  // When filters are provided, we fetch facets for the filtered result set
  // but ensure currently selected values remain in the options
  const fetchFacets = useCallback(async (currentFilters = null, preserveSelected = true, filterConfig = systemShopFilters) => {
    const headers = getAuthHeaders();
    if (!headers['X-API-Key'] && !headers['Authorization']) {
      console.warn('No API key available, skipping facet fetch');
      return;
    }

    setFacetsLoading(true);
    try {
      const selectedFiltersObj = currentFilters || {};
      const apiFilters = buildSearchFilters(selectedFiltersObj);
      const facetKey = JSON.stringify({
        filters: apiFilters,
        matchTypes: systemShopMatchTypes,
        preserveSelected: !!preserveSelected,
        filterKeys: (filterConfig || []).map((f) => f.key),
      });
      const now = Date.now();
      if (
        lastFacetRequestRef.current.key === facetKey &&
        now - lastFacetRequestRef.current.ts < 500
      ) {
        setFacetsLoading(false);
        return;
      }
      lastFacetRequestRef.current = { key: facetKey, ts: now };

      const requestFacets = async (payload) => {
        const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/solr/search`, {
          method: 'POST',
          headers,
          body: JSON.stringify(payload),
        });

        if (!resp.ok) {
          const errorData = await resp.json().catch(() => ({}));
          console.error('Facet fetch failed:', resp.status, errorData);
          throw new Error(errorData.detail || `HTTP ${resp.status}`);
        }
        return resp.json();
      };

      const payload = {
        query: '*:*',
        page: 1,
        page_size: 1,
        filters: apiFilters,
        include_product_attr_facets: true,
        match_types: systemShopMatchTypes,
        context: 'system_shop',
      };
      const data = await requestFacets(payload);
      const newFacets = processFacets(
        data.facets || {},
        data.product_attribute_facets || {},
        filterConfig
      );

      const missingKeys = filterConfig
        .filter((filter) => (newFacets[filter.key] || []).length === 0)
        .map((filter) => filter.key);
      if (missingKeys.length) {
        const fallbackPayload = { ...payload };
        delete fallbackPayload.match_types;
        const fallbackData = await requestFacets(fallbackPayload);
        const fallbackFacets = processFacets(
          fallbackData.facets || {},
          fallbackData.product_attribute_facets || {},
          filterConfig
        );
        missingKeys.forEach((key) => {
          if ((fallbackFacets[key] || []).length) {
            newFacets[key] = fallbackFacets[key];
          }
        });
      }
      
      // If we have current filters and preserveSelected is true, ensure selected values stay in the list
      if (preserveSelected && selectedFiltersObj) {
        filterConfig.forEach((filter) => {
          const selected = selectedFiltersObj[filter.key] || [];
          if (!selected.length) return;
          const currentValues = newFacets[filter.key] || [];
          const currentValueKeys = currentValues.map((item) => String(item.value));
          const missing = selected.filter((v) => !currentValueKeys.includes(String(v)));
          if (missing.length) {
            const augmented = [
              ...missing.map((value) => ({ value, count: null })),
              ...currentValues,
            ];
            newFacets[filter.key] = sortFacetItems(
              augmented,
              filter.sort_order || 'asc',
              filter.value_type || 'string'
            );
          }
        });
      }
      
      setFacetData(newFacets);
    } catch (err) {
      console.error('Error fetching facets:', err);
      message.error(`Failed to load filter options: ${err.message}`);
    } finally {
      setFacetsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAuthHeaders, buildSearchFilters, systemShopFilters, systemShopMatchTypes]);

  const loadSystemShopConfig = useCallback(async () => {
    const headers = getAuthHeaders();
    if (!headers || (!headers['X-API-Key'] && !headers['Authorization'])) {
      return;
    }
    setSystemShopConfigLoading(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/config`, { headers });
      if (!resp.ok) {
        return;
      }
      const data = await resp.json().catch(() => ({}));
      const filters = normalizeSystemShopFilters(data?.config?.system_shop_filters);
      setSystemShopFilters(filters);
      setSystemShopSearch(normalizeSystemShopSearch(data?.config?.system_shop_search));
      setSystemShopTheme(normalizeTheme(data?.config?.system_shop_theme));
      const resolvedMatchTypes = normalizeSystemShopMatchTypes(
        data?.config?.system_shop_filters_settings?.match_types,
        { includeUnmatched: !!data?.config?.system_shop_filters_settings?.include_unmatched }
      );
      setSystemShopMatchTypes(resolvedMatchTypes);
      setSystemShopFilterOrder(
        Array.isArray(data?.config?.system_shop_filters_settings?.order)
          ? data.config.system_shop_filters_settings.order
          : []
      );
      const advancedFilters = normalizeSystemShopAdvancedFilters(data?.config?.system_shop_advanced_filters);
      setSystemShopAdvancedFilters(advancedFilters);
      setSelectedAdvancedFilters((prev) => {
        const next = {};
        advancedFilters.forEach((filter) => {
          next[filter.key] = Array.isArray(prev?.[filter.key]) ? prev[filter.key] : [];
        });
        return next;
      });
      setFacetData((prev) => ({ ...buildEmptyFilterState(filters), ...prev }));
      setSelectedFilters((prev) => mergeFilterState(prev, filters));
      let usedCache = false;
      try {
        const cacheResp = await fetch(`${API_BASE_URL}/api/v1/ahri/system-shop/filters-cache`, { headers });
        if (cacheResp.ok) {
          const cacheData = await cacheResp.json().catch(() => ({}));
          const cached = cacheData?.cache || {};
          if (cached.facets) {
            const cachedFacets = processFacets(
              cached.facets || {},
              cached.product_attribute_facets || {},
              filters
            );
            const hasValues = Object.values(cachedFacets || {}).some(
              (list) => Array.isArray(list) && list.length > 0
            );
            if (hasValues) {
              setFacetData(cachedFacets);
              usedCache = true;
            }
          }
        }
      } catch (err) {
        console.warn('Failed to load cached facets:', err);
      }
      if (!usedCache) {
        await fetchFacets(null, true, filters);
      }
      return resolvedMatchTypes;
    } catch (err) {
      console.error('Failed to load System Shop config:', err);
    } finally {
      setSystemShopConfigLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAuthHeaders]);

  // Fetch results based on selected filters
  // By default (no filters), show AHRI systems with product matches, complete matches first, ordered by brand
  const fetchResults = useCallback(async (pageNum = 1, queryOverride = null, matchTypesOverride = null) => {
    const headers = getAuthHeaders();
    if (!headers['X-API-Key'] && !headers['Authorization']) {
      console.warn('No API key available, skipping results fetch');
      return;
    }

    setResultsLoading(true);
    try {
      const filters = buildSearchFilters(selectedFilters);

      // Build filters for selected facets
      systemShopFilters.forEach((filter) => {
        const values = selectedFilters[filter.key] || [];
        if (!Array.isArray(values) || values.length === 0) return;
        const clause = buildFieldGroupClause(
          filter.fields || [],
          values,
          filter.value_type === 'number'
        );
        if (!clause) return;
        filters._raw_fq = filters._raw_fq || [];
        filters._raw_fq.push(clause);
      });

      const effectiveQuery = queryOverride !== null ? queryOverride : searchTerm;
      const payload = {
        query: effectiveQuery?.trim() ? effectiveQuery.trim() : '*:*',
        page: pageNum,
        page_size: pageSize,
        filters,
        include_product_attr_facets: true,
        // Sort: complete matches first (desc), then by outdoor brand name (asc)
        sort: 'rows_ahri_complete_match desc, rows_outdoorbrandname asc',
        context: 'system_shop',
        search_config: systemShopSearch,
      };
      if (systemShopSearch?.query_type) {
        payload.query_type = systemShopSearch.query_type;
      }

      payload.match_types = matchTypesOverride || systemShopMatchTypes;

      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/solr/search`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });
      const data = await resp.json();

      if (resp.ok) {
        const docs = data.docs || [];
        setResults(docs);
        setTotalFound(data.numFound || 0);
        setPage(pageNum);
        
        // Extract product codes from results and fetch product details
        const productCodes = new Set();
        docs.forEach((doc) => {
          // Extract from prodmap_* fields
          Object.keys(doc).forEach((key) => {
            if (key.startsWith('prodmap_')) {
              const values = Array.isArray(doc[key]) ? doc[key] : [doc[key]];
              values.forEach((v) => {
                if (v && typeof v === 'string') productCodes.add(v.trim());
              });
            }
          });
        });
        
        // Fetch product details for codes not already cached
        if (productCodes.size > 0) {
          const codesToFetch = [...productCodes].filter((c) => !productDetails[c]);
          if (codesToFetch.length > 0) {
            try {
              const prodResp = await fetch(`${API_BASE_URL}/api/v1/ahri/products/lookup`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ product_codes: codesToFetch }),
              });
              if (prodResp.ok) {
                const prodData = await prodResp.json();
                if (prodData.products) {
                  setProductDetails((prev) => ({ ...prev, ...prodData.products }));
                }
              }
            } catch (prodErr) {
              console.warn('Failed to fetch product details:', prodErr);
            }
          }
        }
      } else {
        message.error(data.detail || 'Search failed');
      }
    } catch (err) {
      console.error('Error fetching results:', err);
      message.error('Failed to load systems');
    } finally {
      setResultsLoading(false);
      setResultsFetched(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAuthHeaders, selectedFilters, pageSize, productDetails, systemShopFilters, searchTerm, systemShopSearch, buildSearchFilters, systemShopMatchTypes]);

  // Track if initial load has happened
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [resultsFetched, setResultsFetched] = useState(false);

  useEffect(() => {
    const loadPreview = () => {
      try {
        const raw = localStorage.getItem(scopedKey('theme_preview_ahri_system_shop', getActiveAccountId()));
        if (!raw) {
          setSystemShopPreviewTheme(null);
          return;
        }
        const parsed = JSON.parse(raw);
        setSystemShopPreviewTheme(normalizeTheme(parsed));
      } catch (err) {
        setSystemShopPreviewTheme(null);
      }
    };
    const onPreview = (event) => {
      if (event?.detail?.scope !== 'ahri_system_shop') return;
      if (!event.detail.theme) {
        setSystemShopPreviewTheme(null);
        return;
      }
      setSystemShopPreviewTheme(normalizeTheme(event.detail.theme));
    };
    const onStorage = (event) => {
      if (event.key !== scopedKey('theme_preview_ahri_system_shop', getActiveAccountId())) return;
      loadPreview();
    };
    loadPreview();
    window.addEventListener('theme-preview', onPreview);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('theme-preview', onPreview);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  // Initial load - wait for auth to be ready
  useEffect(() => {
    if (!authLoading && isAuthenticated && !initialLoadDone) {
      (async () => {
        isBootstrappingRef.current = true;
        // loadSystemShopConfig's setSystemShopMatchTypes() hasn't re-rendered yet at this
        // point, so fetchResults below would still close over the pre-config default
        // (['complete','partial']) instead of the account's configured match_types unless
        // we pass the freshly resolved value through explicitly.
        const resolvedMatchTypes = await loadSystemShopConfig();
        await fetchResults(1, null, resolvedMatchTypes); // Load initial results (systems with product matches)
        isBootstrappingRef.current = false;
      })();
      setInitialLoadDone(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAuthenticated, initialLoadDone, loadSystemShopConfig, fetchResults]);

  // Fetch results when filters change (skip initial - already handled above)
  const scheduleFilterRefresh = useCallback((refreshFacets = true) => {
    if (filterRefreshTimerRef.current) {
      clearTimeout(filterRefreshTimerRef.current);
    }
    filterRefreshTimerRef.current = setTimeout(() => {
      if (refreshFacets) {
        fetchFacets(selectedFilters);
      }
      fetchResults(1);
    }, 200);
  }, [fetchFacets, fetchResults, selectedFilters]);

  const loadAdvancedFilterCounts = useCallback(
    async (filter, advancedOverride = null) => {
      if (!filter?.key) return;
      if (advancedCountsLoading[filter.key]) return;
      const headers = getAuthHeaders();
      if (!headers['X-API-Key'] && !headers['Authorization']) return;
      setAdvancedCountsLoading((prev) => ({ ...prev, [filter.key]: true }));
      try {
        const currentAdvanced =
          advancedOverride && typeof advancedOverride === 'object'
            ? advancedOverride
            : selectedAdvancedFilters;
        const advancedWithoutCurrent = { ...(currentAdvanced || {}) };
        advancedWithoutCurrent[filter.key] = [];
        const baseFilters = buildSearchFilters(selectedFilters, advancedWithoutCurrent);
        const entries = filter.values || [];
        const runWithConcurrency = async (items, limit, worker) => {
          const results = [];
          let index = 0;
          const runners = new Array(Math.min(limit, items.length)).fill(null).map(async () => {
            while (index < items.length) {
              const currentIndex = index;
              index += 1;
              results[currentIndex] = await worker(items[currentIndex]);
            }
          });
          await Promise.all(runners);
          return results;
        };

        const results = await runWithConcurrency(entries, 4, async (entry) => {
          const query = entry.query;
          const value = entry.value || entry.label;
          if (!query || !value) {
            return { value, count: null };
          }
          const payload = {
            query: '*:*',
            page: 1,
            page_size: 1,
            filters: { ...baseFilters, _raw_fq: [...(baseFilters._raw_fq || []), query] },
            include_product_attr_facets: false,
            context: 'system_shop',
          };
          if (systemShopSearch?.query_type) {
            payload.query_type = systemShopSearch.query_type;
          }
          payload.match_types = systemShopMatchTypes;
          const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/solr/search`, {
            method: 'POST',
            headers,
            body: JSON.stringify(payload),
          });
          const data = await resp.json().catch(() => ({}));
          if (!resp.ok) {
            return { value, count: null };
          }
          return { value, count: Number(data?.numFound || 0) };
        });
        setAdvancedFilterCounts((prev) => {
          const next = { ...(prev[filter.key] || {}) };
          results.forEach((item) => {
            if (item?.value) {
              next[item.value] = item.count;
            }
          });
          return { ...prev, [filter.key]: next };
        });
      } finally {
        setAdvancedCountsLoading((prev) => ({ ...prev, [filter.key]: false }));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [advancedCountsLoading, buildSearchFilters, getAuthHeaders, systemShopMatchTypes, selectedAdvancedFilters, selectedFilters]
  );

  useEffect(() => {
    if (initialLoadDone && !authLoading && isAuthenticated) {
      const standardSnapshot = JSON.stringify(selectedFilters || {});
      const advancedSnapshot = JSON.stringify(selectedAdvancedFilters || {});
      const changed =
        standardSnapshot !== lastFilterSnapshotRef.current.standard ||
        advancedSnapshot !== lastFilterSnapshotRef.current.advanced;
      if (!changed) {
        return;
      }
      lastFilterSnapshotRef.current = {
        standard: standardSnapshot,
        advanced: advancedSnapshot,
      };
      if (isBootstrappingRef.current) {
        return;
      }
      scheduleFilterRefresh(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilters, selectedAdvancedFilters]);

  useEffect(() => {
    if (!initialLoadDone || authLoading || !isAuthenticated) {
      return;
    }
    const advancedSnapshot = JSON.stringify(selectedAdvancedFilters || {});
    if (advancedSnapshot === lastAdvancedCountsSnapshotRef.current) {
      return;
    }
    lastAdvancedCountsSnapshotRef.current = advancedSnapshot;
    (systemShopAdvancedFilters || []).forEach((filter) => {
      loadAdvancedFilterCounts(filter, selectedAdvancedFilters);
    });
  }, [
    authLoading,
    initialLoadDone,
    isAuthenticated,
    loadAdvancedFilterCounts,
    selectedAdvancedFilters,
    systemShopAdvancedFilters,
  ]);

  useEffect(() => {
    if (!initialLoadDone || authLoading || !isAuthenticated) {
      return;
    }
    const standardSnapshot = JSON.stringify(selectedFilters || {});
    if (standardSnapshot === lastAdvancedCountsStandardSnapshotRef.current) {
      return;
    }
    lastAdvancedCountsStandardSnapshotRef.current = standardSnapshot;
    if (!Object.values(selectedAdvancedFilters || {}).some((vals) => Array.isArray(vals) && vals.length)) {
      return;
    }
    (systemShopAdvancedFilters || []).forEach((filter) => {
      loadAdvancedFilterCounts(filter, selectedAdvancedFilters);
    });
  }, [
    authLoading,
    initialLoadDone,
    isAuthenticated,
    loadAdvancedFilterCounts,
    selectedAdvancedFilters,
    selectedFilters,
    systemShopAdvancedFilters,
  ]);

  useEffect(() => () => {
    if (filterRefreshTimerRef.current) {
      clearTimeout(filterRefreshTimerRef.current);
    }
  }, []);

  useEffect(() => {
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }
    searchTimerRef.current = setTimeout(() => {
      fetchSuggestions(searchTerm);
    }, 300);
    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
    };
  }, [searchTerm, fetchSuggestions]);

  useEffect(() => {
    if (!productDrawerResizing) return undefined;
    const onMove = (event) => {
      const delta = productDrawerResizeRef.current.startX - event.clientX;
      const rawWidth = productDrawerResizeRef.current.startWidth + delta;
      const maxWidth = Math.round(window.innerWidth * 0.75);
      const minWidth = 420;
      setProductDrawerWidth(Math.max(minWidth, Math.min(maxWidth, rawWidth)));
    };
    const onUp = () => setProductDrawerResizing(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [productDrawerResizing]);

  useEffect(() => {
    if (typeof location.state?.restoreScrollY === 'number') {
      window.scrollTo({ top: location.state.restoreScrollY, behavior: 'auto' });
    }
  }, [location.state]);

  const applySearch = useCallback(
    (overrideTerm = null) => {
      if (!authLoading && isAuthenticated) {
        fetchResults(1, overrideTerm);
      }
    },
    [authLoading, isAuthenticated, fetchResults]
  );

  // Handle filter change
  const handleFilterChange = (filterKey, value) => {
    const newFilters = {
      ...selectedFilters,
      [filterKey]: Array.isArray(value) ? value : value ? [value] : [],
    };
    setSelectedFilters(newFilters);
    // Refetch facets with new filter set to narrow down available options
    fetchFacets(newFilters);
  };

  const clearFilterValue = (filterKey, value) => {
    const current = selectedFilters[filterKey] || [];
    const next = current.filter((v) => v !== value);
    const newFilters = {
      ...selectedFilters,
      [filterKey]: next,
    };
    setSelectedFilters(newFilters);
    fetchFacets(newFilters);
  };

  // Clear all filters
  const clearAllFilters = () => {
    const emptyFilters = buildEmptyFilterState(systemShopFilters);
    setSelectedFilters(emptyFilters);
    // Refetch all facets without any filters
    fetchFacets(null);
  };

  // Get active filters for display
  const activeFilters = useMemo(() => {
    const filters = [];
    systemShopFilters.forEach((filter) => {
      const selected = selectedFilters[filter.key] || [];
      selected.forEach((value) => {
        filters.push({
          key: filter.key,
          label: filter.label || filter.key,
          value,
        });
      });
    });
    systemShopAdvancedFilters.forEach((filter) => {
      const selected = selectedAdvancedFilters[filter.key] || [];
      selected.forEach((value) => {
        filters.push({
          key: filter.key,
          label: filter.label || filter.key,
          value,
          type: 'advanced',
        });
      });
    });
    return filters;
  }, [selectedFilters, systemShopFilters, selectedAdvancedFilters, systemShopAdvancedFilters]);

  // Extract system info from AHRI doc
  const parseSystemInfo = (doc) => {
    const payload = doc.payload_json
      ? typeof doc.payload_json === 'string'
        ? JSON.parse(doc.payload_json)
        : doc.payload_json
      : {};

    const getValue = (field) => {
      const arr = doc[field];
      if (Array.isArray(arr) && arr.length) return arr[0];
      const fromPayload = payload[field.replace('rows_', '')];
      if (Array.isArray(fromPayload) && fromPayload.length) return fromPayload[0];
      if (typeof fromPayload === 'string') return fromPayload;
      return payload[field] || '';
    };
    
    // Extract product codes from prodmap_* fields
    const getProductCode = (fieldSuffix) => {
      const field = `prodmap_${fieldSuffix}`;
      const val = doc[field];
      if (Array.isArray(val) && val.length) return val[0];
      if (typeof val === 'string') return val;
      return null;
    };
    
    const outdoorProductCode = getProductCode('outdoormodelnumber');
    const indoorProductCode = getProductCode('indoormodelnumber');
    const furnaceProductCode = getProductCode('furnacemodelnumber');

    // All approved product codes per slot (primary first). componentOptions
    // powers a per-slot picker when an AHRI has multiple approved alternatives.
    const componentOptions = parseComponentOptions(doc, {
      outdoor: getValue('rows_outdoormodelnumber') || payload.outdoorModelNumber,
      indoor: getValue('rows_indoormodelnumber') || payload.indoorModelNumber,
      furnace: getValue('rows_furnacemodelnumber') || payload.furnaceModelNumber,
    });

    // Extract AHRI ID - prefer referenceId from payload, or extract from Solr doc.id (format: "accountId_ahriId")
    const extractAhriId = () => {
      // First try payload fields
      if (payload.referenceId) return payload.referenceId;
      if (payload.ahriId) return payload.ahriId;
      if (payload.ahri_id) return payload.ahri_id;
      if (payload['AHRI ID']) return payload['AHRI ID'];
      // Try Solr rows field
      const refIdField = doc.rows_referenceid;
      if (Array.isArray(refIdField) && refIdField.length) return refIdField[0];
      if (refIdField) return refIdField;
      // Fallback: extract from Solr doc.id (format: "accountId_ahriId")
      const docId = doc.id || '';
      if (docId.includes('_')) {
        return docId.split('_').slice(1).join('_'); // Get everything after first underscore
      }
      return docId;
    };

    return {
      ahriId: extractAhriId(),
      outdoorBrand: decodeHtmlEntities(getValue('rows_outdoorbrandname') || payload.outdoorBrandName),
      indoorBrand: decodeHtmlEntities(getValue('rows_indoorbrandname') || payload.indoorBrandName),
      outdoorModel: getValue('rows_outdoormodelnumber') || payload.outdoorModelNumber,
      indoorModel: getValue('rows_indoormodelnumber') || payload.indoorModelNumber,
      furnaceModel: getValue('rows_furnacemodelnumber') || payload.furnaceModelNumber,
      // Product codes from mapping (primary defaults)
      outdoorProductCode: outdoorProductCode || componentOptions.outdoor.codes[0] || null,
      indoorProductCode: indoorProductCode || componentOptions.indoor.codes[0] || null,
      furnaceProductCode: furnaceProductCode || componentOptions.furnace.codes[0] || null,
      // All approved alternatives per slot + a convenience flag
      componentOptions,
      hasComponentChoices: hasComponentChoices(componentOptions),
      // SEER - try multiple field names (seer2, seerm1, seerm)
      seer2: getValue('rows_seer2') || getValue('rows_seerm1') || getValue('rows_seerm') || payload.seer2 || payload.seerM1 || payload.seerM,
      // HSPF - try multiple field names
      hspf2: getValue('rows_hspf2') || getValue('rows_hspfm') || getValue('rows_hspfm1') || payload.hspf2 || payload.hspfM,
      // EER - try multiple field names (eer2, eer95fm1, eer95fm, eer95f)
      eer2: getValue('rows_eer2') || getValue('rows_eer95fm1') || getValue('rows_eer95fm') || getValue('rows_eer95f') || payload.eer2 || payload.eer95FM1 || payload.eer95FM,
      coolingCapacity: getValue('rows_coolingcapacity95fm') || getValue('rows_coolingcapacity95fm1') || payload.coolingCapacity95FM || payload.coolingCapacity95FM1,
      programName: getValue('rows_programname') || payload.programName,
      region: getValue('rows_region') || payload.region,
      isEnergyStar: getValue('rows_isenergystar') === 'true' || payload.isEnergyStar,
      completeMatch: doc.rows_ahri_complete_match?.includes('True') || doc.rows_ahri_complete_match?.includes(true),
      partialMatch: doc.rows_ahri_partial_match?.includes('True') || doc.rows_ahri_partial_match?.includes(true),
      productMapping: doc.prodmap_search_text || [],
      matchedProductAttrs: doc.matched_product_attributes_json
        ? typeof doc.matched_product_attributes_json === 'string'
          ? JSON.parse(doc.matched_product_attributes_json)
          : doc.matched_product_attributes_json
        : {},
      payload,
    };
  };

  const getCompareKey = (doc, system, idx) => system?.ahriId || doc?.id || `system-${idx}`;

  const toggleCompareSelection = (doc, system, idx) => {
    const key = getCompareKey(doc, system, idx);
    setCompareSelections((prev) => {
      const exists = prev.find((item) => item.key === key);
      if (exists) {
        return prev.filter((item) => item.key !== key);
      }
      if (prev.length >= 3) {
        message.warning('You can compare up to 3 AHRI systems.');
        return prev;
      }
      return [...prev, { key, system }];
    });
  };

  const openProductDetail = (productCode, productType, systemContext = null) => {
    if (!productCode) return;
    const detail = productDetails[productCode] || {};
    setSelectedProduct({
      code: productCode,
      type: productType,
      name: detail?.name || productCode,
      data: detail,
      system: systemContext,
    });
    setProductDrawerOpen(true);
  };

  useEffect(() => {
    if (detailModalOpen) {
      setExpandedProductCodes({});
    }
  }, [detailModalOpen, selectedSystem]);

  const toggleProductExpand = (productCode) => {
    if (!productCode) return;
    setExpandedProductCodes((prev) => ({ ...prev, [productCode]: !prev?.[productCode] }));
  };

  // Select a different approved product for one slot of the open system.
  // Updates selectedSystem in place so the modal (and add-to-cart, which reads
  // the *ProductCode fields) immediately reflect the chosen component.
  const selectSystemComponent = (slotType, code) => {
    const normalized = String(code || '').trim();
    if (!normalized) return;
    const fieldByType = {
      outdoor: 'outdoorProductCode',
      indoor: 'indoorProductCode',
      furnace: 'furnaceProductCode',
    };
    const field = fieldByType[slotType];
    if (!field) return;
    setSelectedSystem((prev) => (prev ? { ...prev, [field]: normalized } : prev));
    if (!productDetails[normalized]) {
      (async () => {
        try {
          const headers = getAuthHeaders();
          const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/products/lookup`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ product_codes: [normalized] }),
          });
          if (resp.ok) {
            const data = await resp.json();
            if (data.products) {
              setProductDetails((prev) => ({ ...prev, ...data.products }));
            }
          }
        } catch (err) {
          console.warn('Failed to fetch product details for component selection:', err);
        }
      })();
    }
  };

  // Badge shown on the system card when any slot has multiple approved
  // options, hinting that alternatives can be chosen.
  const renderOptionsBadge = (system) => {
    if (!system?.hasComponentChoices) return null;
    return (
      <Tag
        color="blue"
        style={{ marginLeft: 6, marginRight: 0, fontSize: 11, lineHeight: '16px', padding: '0 6px' }}
      >
        Options
      </Tag>
    );
  };

  // Inline preview of multi-option slots directly on the list card so shoppers
  // can see and tell apart the alternatives without opening Details. The
  // selected/primary option is highlighted; full switching happens in the
  // detail picker.
  const renderCardSlotOptions = (system) => {
    const options = system?.componentOptions || {};
    const selectedByType = {
      outdoor: system?.outdoorProductCode,
      indoor: system?.indoorProductCode,
      furnace: system?.furnaceProductCode,
    };
    const slots = ['outdoor', 'indoor', 'furnace']
      .map((type) => ({
        type,
        label: options[type]?.label || type,
        codes: options[type]?.codes || [],
      }))
      .filter((slot) => slot.codes.length > 1);
    if (!slots.length) return null;
    return (
      <div className="ahri-card-slot-options mt-2">
        {slots.map((slot) => {
          const selected = String(selectedByType[slot.type] || slot.codes[0] || '').trim();
          // Diff the options' attributes so we can show what distinguishes
          // products whose names look identical.
          const optionDiffs = computeOptionDifferentiators(
            slot.codes.map((code) => ({
              code,
              attributes: productDetails[code]?.attributes,
              description: decodeHtmlEntities(productDetails[code]?.description),
              category: productDetails[code]?.category,
            })),
            { maxKeys: 3 }
          );
          const diffKeyLabels = (optionDiffs.labelsByCode[slot.codes[0]] || []).map(
            (entry) => entry.keyLabel
          );
          return (
            <div key={`card-opts-${slot.type}`} className="ahri-card-slot-option-group">
              <Text style={{ fontSize: 11, color: '#2563a8', fontWeight: 600 }}>
                {slot.codes.length} {String(slot.label || '').toLowerCase()} options
                {diffKeyLabels.length ? ` — differ by ${diffKeyLabels.join(', ')}` : ' — choose in Details'}
              </Text>
              <div className="ahri-card-slot-option-chips">
                {slot.codes.map((code) => {
                  const name = decodeHtmlEntities(productDetails[code]?.name) || code;
                  const isSel = code === selected;
                  const diffLabels = optionDiffs.labelsByCode[code] || [];
                  const fallbackDesc =
                    !diffLabels.length && optionDiffs.useDescriptionFallback
                      ? optionDiffs.descriptionByCode[code] || ''
                      : '';
                  const tooltip = [
                    name !== code ? `${name} (${code})` : code,
                    ...diffLabels.map((d) => `${d.keyLabel}: ${d.value}`),
                  ].join(' · ');
                  return (
                    <span
                      key={`card-opt-${slot.type}-${code}`}
                      className={`ahri-card-slot-chip${isSel ? ' is-selected' : ''}`}
                      title={tooltip}
                    >
                      <span className="ahri-card-slot-chip-name">{name}</span>
                      {diffLabels.length > 0 && (
                        <span className="ahri-card-slot-chip-diffs">
                          {diffLabels.map((d) => (
                            <span key={d.key} className="ahri-card-slot-chip-diff">
                              <span className="ahri-card-slot-chip-diff-key">{d.keyLabel}</span>
                              <span className="ahri-card-slot-chip-diff-val">{d.value}</span>
                            </span>
                          ))}
                        </span>
                      )}
                      {fallbackDesc && (
                        <span className="ahri-card-slot-chip-desc">
                          {fallbackDesc.length > 90 ? `${fallbackDesc.slice(0, 90)}…` : fallbackDesc}
                        </span>
                      )}
                    </span>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Add to cart
  const addToCart = (system, type = 'system') => {
    const existing = cart.find((item) => item.ahriId === system.ahriId && item.type === type);
    if (existing) {
      setCart((prev) =>
        prev.map((item) =>
          item.ahriId === system.ahriId && item.type === type
            ? { ...item, quantity: item.quantity + 1 }
            : item
        )
      );
    } else {
      setCart((prev) => [
        ...prev,
        {
          ...system,
          type,
          quantity: 1,
          id: `${system.ahriId}-${type}-${Date.now()}`,
        },
      ]);
    }
    message.success(`Added ${type === 'system' ? 'complete system' : 'product'} to cart`);
  };

  // Remove from cart
  const removeFromCart = (itemId) => {
    setCart((prev) => prev.filter((item) => item.id !== itemId));
  };

  // Update quantity
  const updateQuantity = (itemId, quantity) => {
    if (quantity < 1) {
      removeFromCart(itemId);
      return;
    }
    setCart((prev) =>
      prev.map((item) => (item.id === itemId ? { ...item, quantity } : item))
    );
  };

  // Open system detail
  const openSystemDetail = async (doc) => {
    const system = parseSystemInfo(doc);
    setSelectedSystem(system);
    setDetailModalOpen(true);
    
    // Fetch product details for every approved alternative across all slots
    // (not just the primary), so the component picker can show each option.
    const options = system.componentOptions || {};
    const productCodes = Array.from(
      new Set(
        ['outdoor', 'indoor', 'furnace']
          .flatMap((slot) => options[slot]?.codes || [])
          .concat([system.outdoorProductCode, system.indoorProductCode, system.furnaceProductCode])
          .map((c) => String(c || '').trim())
          .filter(Boolean)
      )
    );
    const codesToFetch = productCodes.filter((c) => !productDetails[c]);
    
    if (codesToFetch.length > 0) {
      try {
        const headers = getAuthHeaders();
        const prodResp = await fetch(`${API_BASE_URL}/api/v1/ahri/products/lookup`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ product_codes: codesToFetch }),
        });
        if (prodResp.ok) {
          const prodData = await prodResp.json();
          if (prodData.products) {
            setProductDetails((prev) => ({ ...prev, ...prodData.products }));
          }
        }
      } catch (err) {
        console.warn('Failed to fetch product details for modal:', err);
      }
    }
  };

  // Cart total items
  const cartItemCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  // Show loading while auth is being checked
  if (authLoading) {
    return (
      <div className="text-center py-5">
        <Spin size="large" />
        <div className="mt-3">
          <Text type="secondary">Loading...</Text>
        </div>
      </div>
    );
  }

  const renderResults = () => (
    <div className="mb-4">
      {resultsLoading || !resultsFetched ? (
        <div className="text-center py-5">
          <Spin size="large" />
          <div className="mt-3">
            <Text type="secondary">Loading systems...</Text>
          </div>
        </div>
      ) : results.length === 0 && resultsFetched ? (
        <Card className="text-center py-5">
          <Empty
            description={
              Object.values(selectedFilters).some((v) => v !== null)
                ? "No systems found matching your criteria"
                : "No AHRI systems with product matches found. Please ensure AHRI data has been synced and products have been mapped."
            }
          />
        </Card>
      ) : results.length === 0 ? (
        <div className="py-4" />
      ) : (
        <>
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <Text strong>
              Showing {results.length} of {formatNumber(totalFound)} systems
            </Text>
            <Space wrap>
              <div className="d-flex align-items-center gap-2">
                <Text className="text-muted" style={{ fontSize: 12 }}>
                  Page size
                </Text>
                <select
                  className="form-select form-select-sm"
                  style={{ width: 120 }}
                  value={pageSize}
                  onChange={(e) => {
                    const next = Number(e.target.value);
                    setPageSize(next);
                    fetchResults(1);
                  }}
                >
                  {[12, 24, 36, 48].map((size) => (
                    <option key={size} value={size}>
                      {size} / page
                    </option>
                  ))}
                </select>
              </div>
              <Button
                disabled={page <= 1}
                onClick={() => fetchResults(page - 1)}
              >
                Previous
              </Button>
              <Text>Page {page}</Text>
              <Button
                disabled={page * pageSize >= totalFound}
                onClick={() => fetchResults(page + 1)}
              >
                Next
              </Button>
            </Space>
          </div>

          {compareSelections.length > 0 && (
            <div className="compare-bar-inline">
              <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center gap-2">
                <div className="d-flex align-items-center gap-2">
                  <Text strong>Compare</Text>
                  <span className="text-muted small">({compareSelections.length}/3)</span>
                </div>
                <div className="d-flex flex-wrap gap-2 align-items-center">
                  {compareSelections.map((item) => (
                    <Tag
                      key={item.key}
                      closable
                      onClose={() => setCompareSelections((prev) => prev.filter((x) => x.key !== item.key))}
                    >
                      AHRI #{renderAhriIdText(item.system?.ahriId)}
                    </Tag>
                  ))}
                  <Button
                    type="primary"
                    size="small"
                    onClick={() => {
                      if (compareSelections.length < 2) {
                        message.warning('Select at least 2 systems to compare.');
                        return;
                      }
                      navigate('/ahri/shop/compare', {
                        state: {
                          from: { pathname: location.pathname, search: location.search },
                          selections: compareSelections.map((item) => item.system),
                          scrollY: window.scrollY,
                        },
                      });
                    }}
                  >
                    Compare
                  </Button>
                  <Button
                    size="small"
                    onClick={() => setCompareSelections([])}
                  >
                    Clear
                  </Button>
                </div>
              </div>
            </div>
          )}

          <Row gutter={[16, 16]}>
            {results.map((doc, idx) => {
              const system = parseSystemInfo(doc);
              const compareKey = getCompareKey(doc, system, idx);
              const compareSelected = compareSelections.some((item) => item.key === compareKey);
              return (
                <Col xs={24} sm={12} lg={8} xl={6} key={system.ahriId || idx}>
                  <Card
                    hoverable
                    className="h-100 shadow-sm"
                    data-event-context="system_card"
                    data-ahri-id={String(system.ahriId || '')}
                    data-outdoor-product={String(system.outdoorProductCode || '')}
                    data-indoor-product={String(system.indoorProductCode || '')}
                    data-furnace-product={String(system.furnaceProductCode || '')}
                    data-program-name={String(system.programName || '')}
                    onClick={() => openSystemDetail(doc)}
                    style={{ cursor: 'pointer' }}
                    styles={{
                      body: { padding: 16 },
                    }}
                    actions={[
                      <Tooltip title="View Details" key="view">
                        <Button
                          type="primary"
                          ghost
                          className="ahri-details-btn"
                          onClick={(event) => {
                            event.stopPropagation();
                            openSystemDetail(doc);
                          }}
                        >
                          Details
                        </Button>
                      </Tooltip>,
                      <Tooltip title="Add Complete System to Cart" key="add">
                        <Button
                          type="primary"
                          icon={<ShoppingCartOutlined />}
                          onClick={(event) => {
                            event.stopPropagation();
                            addToCart(system, 'system');
                          }}
                        >
                          Add System
                        </Button>
                      </Tooltip>,
                    ]}
                  >
                    {/* Header - ADA compliant: min 16px for headings, high contrast */}
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div>
                        <Text strong style={{ fontSize: 16, color: '#1a1a1a' }}>
                          AHRI #{renderAhriIdText(system.ahriId)}
                        </Text>
                      </div>
                      <div className="d-flex align-items-start gap-2">
                        <div
                          onClick={(event) => event.stopPropagation()}
                          onKeyDown={(event) => event.stopPropagation()}
                          role="presentation"
                        >
                          <Checkbox
                            checked={compareSelected}
                            onChange={() => toggleCompareSelection(doc, system, idx)}
                          >
                            Compare
                          </Checkbox>
                        </div>
                        <Space size={4}>
                          {system.isEnergyStar && (
                            <Tooltip title="Energy Star Certified">
                              <Tag color="green" style={{ margin: 0, fontSize: 12 }}>
                                ★ Energy Star
                              </Tag>
                            </Tooltip>
                          )}
                          {system.completeMatch && (
                            <Tooltip title="Complete Product Match">
                              <CheckCircleOutlined style={{ color: '#137333', fontSize: 18 }} aria-label="Complete Product Match" />
                            </Tooltip>
                          )}
                        </Space>
                      </div>
                    </div>

                    {/* Brands - ADA compliant: min 14px, contrast ratio 4.5:1 */}
                    <div className="mb-2">
                      <Text style={{ fontSize: 14, color: '#4a4a4a' }}>
                        <strong>Outdoor:</strong> {system.outdoorBrand || '-'}
                      </Text>
                      <br />
                      <Text style={{ fontSize: 14, color: '#4a4a4a' }}>
                        <strong>Indoor:</strong> {system.indoorBrand || '-'}
                      </Text>
                    </div>

                    <Divider style={{ margin: '12px 0' }} />

                    {/* Efficiency Ratings - ADA compliant: min 14px labels, 18px values, WCAG AA contrast */}
                    <Row gutter={[8, 8]}>
                      <Col span={8}>
                        <div className="text-center">
                          <Text style={{ fontSize: 13, color: '#555555', fontWeight: 500 }}>
                            SEER
                          </Text>
                          <div>
                            <Text strong style={{ fontSize: 18, color: '#0958d9' }}>
                              {system.seer2 || '-'}
                            </Text>
                          </div>
                        </div>
                      </Col>
                      <Col span={8}>
                        <div className="text-center">
                          <Text style={{ fontSize: 13, color: '#555555', fontWeight: 500 }}>
                            HSPF
                          </Text>
                          <div>
                            <Text strong style={{ fontSize: 18, color: '#389e0d' }}>
                              {system.hspf2 || '-'}
                            </Text>
                          </div>
                        </div>
                      </Col>
                      <Col span={8}>
                        <div className="text-center">
                          <Text style={{ fontSize: 13, color: '#555555', fontWeight: 500 }}>
                            EER
                          </Text>
                          <div>
                            <Text strong style={{ fontSize: 18, color: '#d48806' }}>
                              {system.eer2 || '-'}
                            </Text>
                          </div>
                        </div>
                      </Col>
                    </Row>

                    <div className="mt-3">
                      {/* Outdoor Product - ADA compliant: min 14px text, high contrast colors */}
                      {system.outdoorProductCode && (
                        <button
                          type="button"
                          className="d-flex align-items-center gap-2 w-100 text-start btn btn-light border-0"
                          style={{ padding: 8, borderRadius: 6 }}
                          onClick={(event) => {
                            event.stopPropagation();
                            openProductDetail(system.outdoorProductCode, 'outdoor', system);
                          }}
                        >
                          {renderImage(
                            productDetails[system.outdoorProductCode]?.image_url,
                            `Outdoor unit: ${productDetails[system.outdoorProductCode]?.name || system.outdoorProductCode}`,
                            44
                          )}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <Text style={{ fontSize: 12, color: '#555555', fontWeight: 600 }}>Outdoor Unit</Text>
                            <div style={{ fontSize: 14, fontWeight: 500, color: '#1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {decodeHtmlEntities(productDetails[system.outdoorProductCode]?.name) || system.outdoorProductCode}
                            </div>
                            <div style={{ fontSize: 12, color: '#555555' }}>
                              {productDetails[system.outdoorProductCode]?.manufacturer_part_number || system.outdoorProductCode}
                              {productDetails[system.outdoorProductCode]?.category && (
                                <> • {productDetails[system.outdoorProductCode].category}</>
                              )}
                            </div>
                          </div>
                        </button>
                      )}
                      {/* Indoor Product */}
                      {system.indoorProductCode && (
                        <button
                          type="button"
                          className="d-flex align-items-center gap-2 w-100 text-start btn btn-light border-0 mt-2"
                          style={{ padding: 8, borderRadius: 6 }}
                          onClick={(event) => {
                            event.stopPropagation();
                            openProductDetail(system.indoorProductCode, 'indoor', system);
                          }}
                        >
                          {renderImage(
                            productDetails[system.indoorProductCode]?.image_url,
                            `Indoor unit: ${productDetails[system.indoorProductCode]?.name || system.indoorProductCode}`,
                            44
                          )}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <Text style={{ fontSize: 12, color: '#555555', fontWeight: 600 }}>Indoor Unit</Text>
                            <div style={{ fontSize: 14, fontWeight: 500, color: '#1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {decodeHtmlEntities(productDetails[system.indoorProductCode]?.name) || system.indoorProductCode}
                            </div>
                            <div style={{ fontSize: 12, color: '#555555' }}>
                              {productDetails[system.indoorProductCode]?.manufacturer_part_number || system.indoorProductCode}
                              {productDetails[system.indoorProductCode]?.category && (
                                <> • {productDetails[system.indoorProductCode].category}</>
                              )}
                            </div>
                          </div>
                        </button>
                      )}
                      {/* Furnace Product */}
                      {system.furnaceProductCode && (
                        <button
                          type="button"
                          className="d-flex align-items-center gap-2 w-100 text-start btn btn-light border-0 mt-2"
                          style={{ padding: 8, borderRadius: 6 }}
                          onClick={(event) => {
                            event.stopPropagation();
                            openProductDetail(system.furnaceProductCode, 'furnace', system);
                          }}
                        >
                          {renderImage(
                            productDetails[system.furnaceProductCode]?.image_url,
                            `Furnace: ${productDetails[system.furnaceProductCode]?.name || system.furnaceProductCode}`,
                            44
                          )}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <Text style={{ fontSize: 12, color: '#555555', fontWeight: 600 }}>Furnace</Text>
                            <div style={{ fontSize: 14, fontWeight: 500, color: '#1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {decodeHtmlEntities(productDetails[system.furnaceProductCode]?.name) || system.furnaceProductCode}
                            </div>
                            <div style={{ fontSize: 12, color: '#555555' }}>
                              {productDetails[system.furnaceProductCode]?.manufacturer_part_number || system.furnaceProductCode}
                              {productDetails[system.furnaceProductCode]?.category && (
                                <> • {productDetails[system.furnaceProductCode].category}</>
                              )}
                            </div>
                          </div>
                        </button>
                      )}
                    </div>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </>
      )}
    </div>
  );
  void renderResults;

  return (
    <ConfigProvider
      theme={{
        algorithm: theme.defaultAlgorithm,
        ...antdTheme,
      }}
    >
      <div className="ahri-system-shop" style={{ padding: '0 8px' }}>
        <div
          className={`alert ${websocketConnected ? 'alert-success' : 'alert-warning'} d-flex flex-wrap align-items-center gap-2 py-2 px-3 mb-3`}
          role="status"
        >
          <span className="fw-semibold">Realtime</span>
          <span className="small">
            {websocketConnected ? 'Connected' : `Status: ${websocketStatus}`}
            {websocketSocketId ? ` • Socket: ${websocketSocketId}` : ''}
          </span>
          {lastRealtimeEvent?.eventName && (
            <span className="small text-truncate" style={{ maxWidth: '520px' }}>
              • Last event: {lastRealtimeEvent.eventName}
              {lastRealtimeEvent.summary ? ` (${lastRealtimeEvent.summary})` : ''}
            </span>
          )}
          {websocketError && <span className="small text-danger">• {websocketError}</span>}
        </div>
        {/* Header */}
        <div className="d-flex flex-column flex-xl-row align-items-xl-center justify-content-between gap-3 mb-3">
          <div>
            <Title level={2} style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 12 }}>
              <SafetyCertificateOutlined style={{ color: '#1890ff' }} />
              AHRI Certified System Shop
            </Title>
            <Text type="secondary">
              Find and order AHRI certified HVAC systems with matching products
            </Text>
          </div>
          {systemShopSearch?.enabled && (
            <div className="ahri-search-inline">
              <AutoComplete
                options={searchSuggestions}
                value={searchTerm}
                onChange={setSearchTerm}
                onSelect={(value) => {
                  setSearchTerm(value);
                  applySearch(value);
                }}
                style={{ width: '100%' }}
              >
                <Input
                  placeholder={systemShopSearch.placeholder || 'Search systems'}
                  allowClear
                  onPressEnter={() => applySearch(searchTerm)}
                />
              </AutoComplete>
              <div className="d-flex gap-2 mt-2 mt-lg-0">
                <Button type="primary" onClick={() => applySearch(searchTerm)} loading={resultsLoading}>
                  Search
                </Button>
                <Button
                  onClick={() => {
                    setSearchTerm('');
                    setSearchSuggestions([]);
                    applySearch('');
                  }}
                >
                  Clear
                </Button>
              </div>
              {searching && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Fetching suggestions…
                </Text>
              )}
            </div>
          )}
          <Badge count={cartItemCount} showZero>
            <Button
              type="primary"
              size="large"
              icon={<ShoppingCartOutlined />}
              onClick={() => setCartDrawerOpen(true)}
            >
              Cart
            </Button>
          </Badge>
        </div>

        {/* Facet Filters */}
        {(() => {
          const isLeftLayout = filterLayout === 'left';
          const filterContent = (
            <>
              <div className="d-flex align-items-center flex-wrap gap-2 mb-2">
                <FilterOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                <Text strong style={{ fontSize: 16 }}>
                  Filter Systems
                </Text>
                {account && (
                  <Text style={{ marginLeft: 12, fontSize: 14, color: '#555555' }}>
                    (Account: {account.name || account.id})
                  </Text>
                )}
                {systemShopConfigLoading && (
                  <Text style={{ marginLeft: 12, fontSize: 12, color: '#888888' }}>
                    Loading filter config…
                  </Text>
                )}
                {activeFilters.length > 0 && (
                  <Button
                    type="link"
                    size="small"
                    onClick={clearAllFilters}
                    style={{ marginLeft: 8 }}
                  >
                    Clear All
                  </Button>
                )}
                <Button
                  type="text"
                  size="small"
                  onClick={() => setFiltersCollapsed((prev) => !prev)}
                >
                  {filtersCollapsed ? 'Show Filters' : 'Hide Filters'}
                </Button>
                <div className="btn-group btn-group-sm">
                  <Tooltip title="Filters on top">
                    <Button
                      type={filterLayout === 'top' ? 'primary' : 'default'}
                      icon={<BorderBottomOutlined />}
                      onClick={() => setFilterLayout('top')}
                    />
                  </Tooltip>
                  <Tooltip title="Filters on left">
                    <Button
                      type={filterLayout === 'left' ? 'primary' : 'default'}
                      icon={<BorderLeftOutlined />}
                      onClick={() => setFilterLayout('left')}
                    />
                  </Tooltip>
                </div>
                <Button
                  type="text"
                  icon={<ReloadOutlined spin={facetsLoading} />}
                  onClick={() => fetchFacets(selectedFilters)}
                  style={{ marginLeft: 8 }}
                />
              </div>

              {/* Show message if no AHRI data */}
              {!filtersCollapsed &&
                !facetsLoading &&
                resultsFetched &&
                Object.values(facetData || {}).every((values) => !values || values.length === 0) && (
                <div className="alert alert-warning mb-3">
                  <strong>No AHRI data available.</strong> Please ensure AHRI data has been synced and indexed for this account.
                  Go to <a href="/ahri">AHRI Data</a> → Solr tab to import data.
                </div>
              )}

              {!filtersCollapsed && (
                <Row gutter={[12, 12]} className={isLeftLayout ? 'filter-stack' : ''}>
                  {orderedFilters.map((item) => {
                    if (item.type === 'advanced') {
                      return (
                        <Col xs={24} sm={isLeftLayout ? 24 : 12} md={isLeftLayout ? 24 : 8} lg={isLeftLayout ? 24 : 6} xl={isLeftLayout ? 24 : 4} key={`advanced-${item.key}`}>
                          <div className="d-flex flex-column gap-1">
                            <Text style={{ fontSize: 13, color: '#0f766e', fontWeight: 600 }}>
                              {item.label || item.key}
                            </Text>
                            {renderAdvancedDropdown(item.entry)}
                          </div>
                        </Col>
                      );
                    }
                    const filter = item.entry;
                    const label = filter.label || filter.key;
                    const options = facetData[filter.key] || [];
                    const visibleOptions =
                      filter.value_type === 'number' ? options.slice(0, 30) : options;
                    return (
                      <Col xs={24} sm={isLeftLayout ? 24 : 12} md={isLeftLayout ? 24 : 8} lg={isLeftLayout ? 24 : 6} xl={isLeftLayout ? 24 : 4} key={filter.key}>
                        <div className="d-flex flex-column gap-1">
                          <Text style={{ fontSize: 13, color: '#475569', fontWeight: 600 }}>{label}</Text>
                          {renderFacetDropdown(
                            label,
                            visibleOptions,
                            selectedFilters[filter.key],
                            (vals) => handleFilterChange(filter.key, vals),
                            filter,
                            filter.key,
                            facetsLoading
                          )}
                        </div>
                      </Col>
                    );
                  })}
                </Row>
              )}

              {/* Active Filters Tags - ADA compliant */}
              {activeFilters.length > 0 && (
                <div className={filtersCollapsed ? 'mt-1' : 'mt-3'} role="region" aria-label="Active filters">
                  <Text style={{ marginRight: 8, fontSize: 14, color: '#333333', fontWeight: 500 }}>
                    Active Filters:
                  </Text>
                  <Space wrap>
                    {activeFilters.map((filter) => {
                      const isAdvanced = filter.type === 'advanced';
                      const filterConfig = isAdvanced
                        ? { value_display_map: {} }
                        : systemShopFilters.find((entry) => entry.key === filter.key);
                      const displayValue = getDisplayValue(filterConfig, filter.value);
                      return (
                      <Tag
                        key={`${filter.key}:${filter.value}:${filter.type || 'standard'}`}
                        closable
                        onClose={() =>
                          isAdvanced
                            ? setSelectedAdvancedFilters((prev) => ({
                                ...prev,
                                [filter.key]: (prev?.[filter.key] || []).filter((val) => val !== filter.value),
                              }))
                            : clearFilterValue(filter.key, filter.value)
                        }
                        color="blue"
                        style={{ margin: 0, fontSize: 14 }}
                        aria-label={`Remove ${filter.label} filter: ${filter.value}`}
                      >
                        {filter.label}: {displayValue}
                      </Tag>
                      );
                    })}
                  </Space>
                </div>
              )}
            </>
          );

          if (filterLayout === 'left') {
            return (
              <Row gutter={[16, 16]} className="mb-3">
                <Col xs={24} lg={6} xl={5}>
                  <Card className="shadow-sm" styles={{ body: { padding: '12px 16px' } }}>
                    {filterContent}
                  </Card>
                </Col>
                <Col xs={24} lg={18} xl={19}>
                  {/* Results */}
                  <div className="mb-4">
                    {resultsLoading || !resultsFetched ? (
                      <div className="text-center py-5">
                        <Spin size="large" />
                        <div className="mt-3">
                          <Text type="secondary">Loading systems...</Text>
                        </div>
                      </div>
                    ) : results.length === 0 ? (
                      <Card className="text-center py-5">
                        <Empty 
                          description={
                            Object.values(selectedFilters).some((v) => v !== null)
                              ? "No systems found matching your criteria"
                              : "No AHRI systems with product matches found. Please ensure AHRI data has been synced and products have been mapped."
                          }
                        />
                      </Card>
                    ) : (
                      <>
                        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                          <Text strong>
                            Showing {results.length} of {formatNumber(totalFound)} systems
                          </Text>
                          <Space wrap>
                            <div className="d-flex align-items-center gap-2">
                              <Text className="text-muted" style={{ fontSize: 12 }}>
                                Page size
                              </Text>
                              <select
                                className="form-select form-select-sm"
                                style={{ width: 120 }}
                                value={pageSize}
                                onChange={(e) => {
                                  const next = Number(e.target.value);
                                  setPageSize(next);
                                  fetchResults(1);
                                }}
                              >
                                {[12, 24, 36, 48].map((size) => (
                                  <option key={size} value={size}>
                                    {size} / page
                                  </option>
                                ))}
                              </select>
                            </div>
                            <Button
                              disabled={page <= 1}
                              onClick={() => fetchResults(page - 1)}
                            >
                              Previous
                            </Button>
                            <Text>Page {page}</Text>
                            <Button
                              disabled={page * pageSize >= totalFound}
                              onClick={() => fetchResults(page + 1)}
                            >
                              Next
                            </Button>
                          </Space>
                        </div>

                        {compareSelections.length > 0 && (
                          <div className="compare-bar-inline">
                            <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center gap-2">
                              <div className="d-flex align-items-center gap-2">
                                <Text strong>Compare</Text>
                                <span className="text-muted small">({compareSelections.length}/3)</span>
                              </div>
                              <div className="d-flex flex-wrap gap-2 align-items-center">
                                {compareSelections.map((item) => (
                                  <Tag
                                    key={item.key}
                                    closable
                                    onClose={() => setCompareSelections((prev) => prev.filter((x) => x.key !== item.key))}
                                  >
                                    AHRI #{renderAhriIdText(item.system?.ahriId)}
                                  </Tag>
                                ))}
                                <Button
                                  type="primary"
                                  size="small"
                                  onClick={() => {
                                    if (compareSelections.length < 2) {
                                      message.warning('Select at least 2 systems to compare.');
                                      return;
                                    }
                                    navigate('/ahri/shop/compare', {
                                      state: {
                                        from: { pathname: location.pathname, search: location.search },
                                        selections: compareSelections.map((item) => item.system),
                                        scrollY: window.scrollY,
                                      },
                                    });
                                  }}
                                >
                                  Compare
                                </Button>
                                <Button
                                  size="small"
                                  onClick={() => setCompareSelections([])}
                                >
                                  Clear
                                </Button>
                              </div>
                            </div>
                          </div>
                        )}

                        <Row gutter={[16, 16]}>
                          {results.map((doc, idx) => {
                            const system = parseSystemInfo(doc);
                            const compareKey = getCompareKey(doc, system, idx);
                            const compareSelected = compareSelections.some((item) => item.key === compareKey);
                            return (
                              <Col xs={24} sm={12} lg={8} xl={6} key={system.ahriId || idx}>
                                <Card
                                  hoverable
                                  className="h-100 shadow-sm"
                                  data-event-context="system_card"
                                  data-ahri-id={String(system.ahriId || '')}
                                  data-outdoor-product={String(system.outdoorProductCode || '')}
                                  data-indoor-product={String(system.indoorProductCode || '')}
                                  data-furnace-product={String(system.furnaceProductCode || '')}
                                  data-program-name={String(system.programName || '')}
                                  onClick={() => openSystemDetail(doc)}
                                  style={{ cursor: 'pointer' }}
                                  styles={{
                                    body: { padding: 16 },
                                  }}
                                  actions={[
                                    <Tooltip title="View Details" key="view">
                                      <Button
                                        type="primary"
                                        ghost
                                        className="ahri-details-btn"
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          openSystemDetail(doc);
                                        }}
                                      >
                                        Details
                                        {renderOptionsBadge(system)}
                                      </Button>
                                    </Tooltip>,
                                    <Tooltip title="Add Complete System to Cart" key="add">
                                      <Button
                                        type="primary"
                                        icon={<ShoppingCartOutlined />}
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          addToCart(system, 'system');
                                        }}
                                      >
                                        Add System
                                      </Button>
                                    </Tooltip>,
                                  ]}
                                >
                                  {/* Header - ADA compliant: min 16px for headings, high contrast */}
                                  <div className="d-flex justify-content-between align-items-start mb-2">
                                    <div>
                                      <Text strong style={{ fontSize: 16, color: '#1a1a1a' }}>
                                        AHRI #{renderAhriIdText(system.ahriId)}
                                      </Text>
                                    </div>
                                    <div className="d-flex align-items-start gap-2">
                                      <div
                                        onClick={(event) => event.stopPropagation()}
                                        onKeyDown={(event) => event.stopPropagation()}
                                        role="presentation"
                                      >
                                        <Checkbox
                                          checked={compareSelected}
                                          onChange={() => toggleCompareSelection(doc, system, idx)}
                                        >
                                          Compare
                                        </Checkbox>
                                      </div>
                                      <Space size={4}>
                                        {system.isEnergyStar && (
                                          <Tooltip title="Energy Star Certified">
                                            <Tag color="green" style={{ margin: 0, fontSize: 12 }}>
                                              ★ Energy Star
                                            </Tag>
                                          </Tooltip>
                                        )}
                                        {system.completeMatch && (
                                          <Tooltip title="Complete Product Match">
                                            <CheckCircleOutlined style={{ color: '#137333', fontSize: 18 }} aria-label="Complete Product Match" />
                                          </Tooltip>
                                        )}
                                      </Space>
                                    </div>
                                  </div>

                                  {/* Brands - ADA compliant: min 14px, contrast ratio 4.5:1 */}
                                  <div className="mb-2">
                                    <Text style={{ fontSize: 14, color: '#4a4a4a' }}>
                                      <strong>Outdoor:</strong> {system.outdoorBrand || '-'}
                                    </Text>
                                    <br />
                                    <Text style={{ fontSize: 14, color: '#4a4a4a' }}>
                                      <strong>Indoor:</strong> {system.indoorBrand || '-'}
                                    </Text>
                                  </div>

                                  <Divider style={{ margin: '12px 0' }} />

                                  {/* Efficiency Ratings - ADA compliant: min 14px labels, 18px values, WCAG AA contrast */}
                                  <Row gutter={[8, 8]}>
                                    <Col span={8}>
                                      <div className="text-center">
                                        <Text type="secondary" style={{ fontSize: 13 }}>SEER2</Text>
                                        <div style={{ fontSize: 18, fontWeight: 600, color: '#1a1a1a' }}>
                                          {system.seer2 || '-'}
                                        </div>
                                      </div>
                                    </Col>
                                    <Col span={8}>
                                      <div className="text-center">
                                        <Text type="secondary" style={{ fontSize: 13 }}>HSPF2</Text>
                                        <div style={{ fontSize: 18, fontWeight: 600, color: '#1a1a1a' }}>
                                          {system.hspf2 || '-'}
                                        </div>
                                      </div>
                                    </Col>
                                    <Col span={8}>
                                      <div className="text-center">
                                        <Text type="secondary" style={{ fontSize: 13 }}>EER2</Text>
                                        <div style={{ fontSize: 18, fontWeight: 600, color: '#1a1a1a' }}>
                                          {system.eer2 || '-'}
                                        </div>
                                      </div>
                                    </Col>
                                  </Row>
                                  <div className="mt-3">
                                    {/* Outdoor Product - ADA compliant: min 14px text, high contrast colors */}
                                    {system.outdoorProductCode && (
                                      <button
                                        type="button"
                                        className="d-flex align-items-center gap-2 w-100 text-start btn btn-light border-0"
                                        style={{ padding: 8, borderRadius: 6 }}
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          openProductDetail(system.outdoorProductCode, 'outdoor', system);
                                        }}
                                      >
                                        {renderImage(
                                          productDetails[system.outdoorProductCode]?.image_url,
                                          `Outdoor unit: ${productDetails[system.outdoorProductCode]?.name || system.outdoorProductCode}`,
                                          44
                                        )}
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                          <Text style={{ fontSize: 12, color: '#555555', fontWeight: 600 }}>Outdoor Unit</Text>
                                          <div style={{ fontSize: 14, fontWeight: 500, color: '#1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {decodeHtmlEntities(productDetails[system.outdoorProductCode]?.name) || system.outdoorProductCode}
                                          </div>
                                          <div style={{ fontSize: 12, color: '#555555' }}>
                                            {productDetails[system.outdoorProductCode]?.manufacturer_part_number || system.outdoorProductCode}
                                            {productDetails[system.outdoorProductCode]?.category && (
                                              <> • {productDetails[system.outdoorProductCode].category}</>
                                            )}
                                          </div>
                                        </div>
                                      </button>
                                    )}
                                    {/* Indoor Product */}
                                    {system.indoorProductCode && (
                                      <button
                                        type="button"
                                        className="d-flex align-items-center gap-2 w-100 text-start btn btn-light border-0 mt-2"
                                        style={{ padding: 8, borderRadius: 6 }}
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          openProductDetail(system.indoorProductCode, 'indoor', system);
                                        }}
                                      >
                                        {renderImage(
                                          productDetails[system.indoorProductCode]?.image_url,
                                          `Indoor unit: ${productDetails[system.indoorProductCode]?.name || system.indoorProductCode}`,
                                          44
                                        )}
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                          <Text style={{ fontSize: 12, color: '#555555', fontWeight: 600 }}>Indoor Unit</Text>
                                          <div style={{ fontSize: 14, fontWeight: 500, color: '#1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {decodeHtmlEntities(productDetails[system.indoorProductCode]?.name) || system.indoorProductCode}
                                          </div>
                                          <div style={{ fontSize: 12, color: '#555555' }}>
                                            {productDetails[system.indoorProductCode]?.manufacturer_part_number || system.indoorProductCode}
                                            {productDetails[system.indoorProductCode]?.category && (
                                              <> • {productDetails[system.indoorProductCode].category}</>
                                            )}
                                          </div>
                                        </div>
                                      </button>
                                    )}
                                    {/* Furnace Product */}
                                    {system.furnaceProductCode && (
                                      <button
                                        type="button"
                                        className="d-flex align-items-center gap-2 w-100 text-start btn btn-light border-0 mt-2"
                                        style={{ padding: 8, borderRadius: 6 }}
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          openProductDetail(system.furnaceProductCode, 'furnace', system);
                                        }}
                                      >
                                        {renderImage(
                                          productDetails[system.furnaceProductCode]?.image_url,
                                          `Furnace: ${productDetails[system.furnaceProductCode]?.name || system.furnaceProductCode}`,
                                          44
                                        )}
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                          <Text style={{ fontSize: 12, color: '#555555', fontWeight: 600 }}>Furnace</Text>
                                          <div style={{ fontSize: 14, fontWeight: 500, color: '#1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {decodeHtmlEntities(productDetails[system.furnaceProductCode]?.name) || system.furnaceProductCode}
                                          </div>
                                          <div style={{ fontSize: 12, color: '#555555' }}>
                                            {productDetails[system.furnaceProductCode]?.manufacturer_part_number || system.furnaceProductCode}
                                            {productDetails[system.furnaceProductCode]?.category && (
                                              <> • {productDetails[system.furnaceProductCode].category}</>
                                            )}
                                          </div>
                                        </div>
                                      </button>
                                    )}
                                  </div>
                                  {renderCardSlotOptions(system)}
                                </Card>
                              </Col>
                            );
                          })}
                        </Row>
                      </>
                    )}
                  </div>
                </Col>
              </Row>
            );
          }

          return (
            <Card
              className="mb-3 shadow-sm"
              styles={{ body: { padding: '12px 16px' } }}
            >
              {filterContent}
            </Card>
          );
        })()}

        {filterLayout === 'top' && (
        <>
        {/* Results */}
        <div className="mb-4">
          {resultsLoading || !resultsFetched ? (
            <div className="text-center py-5">
              <Spin size="large" />
              <div className="mt-3">
                <Text type="secondary">Loading systems...</Text>
              </div>
            </div>
          ) : results.length === 0 ? (
            <Card className="text-center py-5">
              <Empty 
                description={
                  Object.values(selectedFilters).some((v) => v !== null)
                    ? "No systems found matching your criteria"
                    : "No AHRI systems with product matches found. Please ensure AHRI data has been synced and products have been mapped."
                }
              />
            </Card>
          ) : (
            <>
              <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <Text strong>
                  Showing {results.length} of {formatNumber(totalFound)} systems
                </Text>
                <Space wrap>
                  <div className="d-flex align-items-center gap-2">
                    <Text className="text-muted" style={{ fontSize: 12 }}>
                      Page size
                    </Text>
                    <select
                      className="form-select form-select-sm"
                      style={{ width: 120 }}
                      value={pageSize}
                      onChange={(e) => {
                        const next = Number(e.target.value);
                        setPageSize(next);
                        fetchResults(1);
                      }}
                    >
                      {[12, 24, 36, 48].map((size) => (
                        <option key={size} value={size}>
                          {size} / page
                        </option>
                      ))}
                    </select>
                  </div>
                  <Button
                    disabled={page <= 1}
                    onClick={() => fetchResults(page - 1)}
                  >
                    Previous
                  </Button>
                  <Text>Page {page}</Text>
                  <Button
                    disabled={page * pageSize >= totalFound}
                    onClick={() => fetchResults(page + 1)}
                  >
                    Next
                  </Button>
                </Space>
              </div>

              {compareSelections.length > 0 && (
                <div className="compare-bar-inline">
                  <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center gap-2">
                    <div className="d-flex align-items-center gap-2">
                      <Text strong>Compare</Text>
                      <span className="text-muted small">({compareSelections.length}/3)</span>
                    </div>
                    <div className="d-flex flex-wrap gap-2 align-items-center">
                      {compareSelections.map((item) => (
                        <Tag
                          key={item.key}
                          closable
                          onClose={() => setCompareSelections((prev) => prev.filter((x) => x.key !== item.key))}
                        >
                          AHRI #{renderAhriIdText(item.system?.ahriId)}
                        </Tag>
                      ))}
                      <Button
                        type="primary"
                        size="small"
                        onClick={() => {
                          if (compareSelections.length < 2) {
                            message.warning('Select at least 2 systems to compare.');
                            return;
                          }
                          navigate('/ahri/shop/compare', {
                            state: {
                              from: { pathname: location.pathname, search: location.search },
                              selections: compareSelections.map((item) => item.system),
                              scrollY: window.scrollY,
                            },
                          });
                        }}
                      >
                        Compare
                      </Button>
                      <Button
                        size="small"
                        onClick={() => setCompareSelections([])}
                      >
                        Clear
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              <Row gutter={[16, 16]}>
                {results.map((doc, idx) => {
                  const system = parseSystemInfo(doc);
                  const compareKey = getCompareKey(doc, system, idx);
                  const compareSelected = compareSelections.some((item) => item.key === compareKey);
                  return (
                    <Col xs={24} sm={12} lg={8} xl={6} key={system.ahriId || idx}>
                      <Card
                        hoverable
                        className="h-100 shadow-sm"
                        data-event-context="system_card"
                        data-ahri-id={String(system.ahriId || '')}
                        data-outdoor-product={String(system.outdoorProductCode || '')}
                        data-indoor-product={String(system.indoorProductCode || '')}
                        data-furnace-product={String(system.furnaceProductCode || '')}
                        data-program-name={String(system.programName || '')}
                        onClick={() => openSystemDetail(doc)}
                        style={{ cursor: 'pointer' }}
                        styles={{
                          body: { padding: 16 },
                        }}
                        actions={[
                          <Tooltip title="View Details" key="view">
                            <Button
                              type="primary"
                              ghost
                              className="ahri-details-btn"
                              onClick={(event) => {
                                event.stopPropagation();
                                openSystemDetail(doc);
                              }}
                            >
                              Details
                              {renderOptionsBadge(system)}
                            </Button>
                          </Tooltip>,
                          <Tooltip title="Add Complete System to Cart" key="add">
                            <Button
                              type="primary"
                              icon={<ShoppingCartOutlined />}
                              onClick={(event) => {
                                event.stopPropagation();
                                addToCart(system, 'system');
                              }}
                            >
                              Add System
                            </Button>
                          </Tooltip>,
                        ]}
                      >
                        {/* Header - ADA compliant: min 16px for headings, high contrast */}
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div>
                            <Text strong style={{ fontSize: 16, color: '#1a1a1a' }}>
                              AHRI #{renderAhriIdText(system.ahriId)}
                            </Text>
                          </div>
                          <div className="d-flex align-items-start gap-2">
                            <div
                              onClick={(event) => event.stopPropagation()}
                              onKeyDown={(event) => event.stopPropagation()}
                              role="presentation"
                            >
                              <Checkbox
                                checked={compareSelected}
                                onChange={() => toggleCompareSelection(doc, system, idx)}
                              >
                                Compare
                              </Checkbox>
                            </div>
                            <Space size={4}>
                              {system.isEnergyStar && (
                                <Tooltip title="Energy Star Certified">
                                  <Tag color="green" style={{ margin: 0, fontSize: 12 }}>
                                    ★ Energy Star
                                  </Tag>
                                </Tooltip>
                              )}
                              {system.completeMatch && (
                                <Tooltip title="Complete Product Match">
                                  <CheckCircleOutlined style={{ color: '#137333', fontSize: 18 }} aria-label="Complete Product Match" />
                                </Tooltip>
                              )}
                            </Space>
                          </div>
                        </div>

                        {/* Brands - ADA compliant: min 14px, contrast ratio 4.5:1 */}
                        <div className="mb-2">
                          <Text style={{ fontSize: 14, color: '#4a4a4a' }}>
                            <strong>Outdoor:</strong> {system.outdoorBrand || '-'}
                          </Text>
                          <br />
                          <Text style={{ fontSize: 14, color: '#4a4a4a' }}>
                            <strong>Indoor:</strong> {system.indoorBrand || '-'}
                          </Text>
                        </div>

                        <Divider style={{ margin: '12px 0' }} />

                        {/* Efficiency Ratings - ADA compliant: min 14px labels, 18px values, WCAG AA contrast */}
                        <Row gutter={8}>
                          <Col span={8}>
                            <div className="text-center">
                              <Text style={{ fontSize: 13, color: '#555555', fontWeight: 500 }}>
                                SEER
                              </Text>
                              <div>
                                <Text strong style={{ fontSize: 18, color: '#0958d9' }}>
                                  {system.seer2 || '-'}
                                </Text>
                              </div>
                            </div>
                          </Col>
                          <Col span={8}>
                            <div className="text-center">
                              <Text style={{ fontSize: 13, color: '#555555', fontWeight: 500 }}>
                                HSPF
                              </Text>
                              <div>
                                <Text strong style={{ fontSize: 18, color: '#137333' }}>
                                  {system.hspf2 || '-'}
                                </Text>
                              </div>
                            </div>
                          </Col>
                          <Col span={8}>
                            <div className="text-center">
                              <Text style={{ fontSize: 13, color: '#555555', fontWeight: 500 }}>
                                EER
                              </Text>
                              <div>
                                <Text strong style={{ fontSize: 18, color: '#b45309' }}>
                                  {system.eer2 || '-'}
                                </Text>
                              </div>
                            </div>
                          </Col>
                        </Row>

                        {/* Products */}
                        <div className="mt-3">
                          {/* Outdoor Product - ADA compliant: min 14px text, high contrast colors */}
                          {system.outdoorProductCode && (
                            <button
                              type="button"
                              className="d-flex align-items-center mb-2 text-start border-0 bg-transparent w-100 p-0"
                              style={{ gap: 10 }}
                              onClick={(event) => {
                                event.stopPropagation();
                                openProductDetail(system.outdoorProductCode, 'outdoor', system);
                              }}
                            >
                              {renderImage(
                                productDetails[system.outdoorProductCode]?.image_url,
                                `Outdoor unit: ${productDetails[system.outdoorProductCode]?.name || system.outdoorProductCode}`,
                                44
                              )}
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <Text style={{ fontSize: 12, color: '#555555', fontWeight: 600 }}>Outdoor Unit</Text>
                                <div style={{ fontSize: 14, fontWeight: 500, color: '#1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {decodeHtmlEntities(productDetails[system.outdoorProductCode]?.name) || system.outdoorProductCode}
                                </div>
                                <div style={{ fontSize: 12, color: '#555555' }}>
                                  {productDetails[system.outdoorProductCode]?.manufacturer_part_number || system.outdoorProductCode}
                                  {productDetails[system.outdoorProductCode]?.category && (
                                    <> • {productDetails[system.outdoorProductCode].category}</>
                                  )}
                                </div>
                              </div>
                            </button>
                          )}
                          {/* Indoor Product - ADA compliant */}
                          {system.indoorProductCode && (
                            <button
                              type="button"
                              className="d-flex align-items-center mb-2 text-start border-0 bg-transparent w-100 p-0"
                              style={{ gap: 10 }}
                              onClick={(event) => {
                                event.stopPropagation();
                                openProductDetail(system.indoorProductCode, 'indoor', system);
                              }}
                            >
                              {renderImage(
                                productDetails[system.indoorProductCode]?.image_url,
                                `Indoor unit: ${productDetails[system.indoorProductCode]?.name || system.indoorProductCode}`,
                                44
                              )}
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <Text style={{ fontSize: 12, color: '#555555', fontWeight: 600 }}>Indoor Unit</Text>
                                <div style={{ fontSize: 14, fontWeight: 500, color: '#1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {decodeHtmlEntities(productDetails[system.indoorProductCode]?.name) || system.indoorProductCode}
                                </div>
                                <div style={{ fontSize: 12, color: '#555555' }}>
                                  {productDetails[system.indoorProductCode]?.manufacturer_part_number || system.indoorProductCode}
                                  {productDetails[system.indoorProductCode]?.category && (
                                    <> • {productDetails[system.indoorProductCode].category}</>
                                  )}
                                </div>
                              </div>
                            </button>
                          )}
                          {/* Furnace Product - ADA compliant */}
                          {system.furnaceProductCode && (
                            <button
                              type="button"
                              className="d-flex align-items-center mb-2 text-start border-0 bg-transparent w-100 p-0"
                              style={{ gap: 10 }}
                              onClick={(event) => {
                                event.stopPropagation();
                                openProductDetail(system.furnaceProductCode, 'furnace', system);
                              }}
                            >
                              {renderImage(
                                productDetails[system.furnaceProductCode]?.image_url,
                                `Furnace: ${productDetails[system.furnaceProductCode]?.name || system.furnaceProductCode}`,
                                44
                              )}
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <Text style={{ fontSize: 12, color: '#555555', fontWeight: 600 }}>Furnace</Text>
                                <div style={{ fontSize: 14, fontWeight: 500, color: '#1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {decodeHtmlEntities(productDetails[system.furnaceProductCode]?.name) || system.furnaceProductCode}
                                </div>
                                <div style={{ fontSize: 12, color: '#555555' }}>
                                  {productDetails[system.furnaceProductCode]?.manufacturer_part_number || system.furnaceProductCode}
                                  {productDetails[system.furnaceProductCode]?.category && (
                                    <> • {productDetails[system.furnaceProductCode].category}</>
                                  )}
                                </div>
                              </div>
                            </button>
                          )}
                          {/* Fallback to model numbers if no product codes */}
                          {!system.outdoorProductCode && !system.indoorProductCode && !system.furnaceProductCode && (
                            <>
                              {system.outdoorModel && (
                                <div>
                                  <Text style={{ fontSize: 13, color: '#555555', fontWeight: 500 }}>
                                    Outdoor:
                                  </Text>{' '}
                                  <Text style={{ fontSize: 14, color: '#1a1a1a' }}>{system.outdoorModel}</Text>
                                </div>
                              )}
                              {system.indoorModel && (
                                <div>
                                  <Text style={{ fontSize: 13, color: '#555555', fontWeight: 500 }}>
                                    Indoor:
                                  </Text>{' '}
                                  <Text style={{ fontSize: 14, color: '#1a1a1a' }}>{system.indoorModel}</Text>
                                </div>
                              )}
                              {system.furnaceModel && (
                                <div>
                                  <Text style={{ fontSize: 13, color: '#555555', fontWeight: 500 }}>
                                    Furnace:
                                  </Text>{' '}
                                  <Text style={{ fontSize: 14, color: '#1a1a1a' }}>{system.furnaceModel}</Text>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                        {renderCardSlotOptions(system)}
                      </Card>
                    </Col>
                  );
                })}
              </Row>

              {/* Pagination */}
              {totalFound > pageSize && (
                <div className="d-flex justify-content-center mt-4">
                  <Space>
                    <Button
                      disabled={page <= 1}
                      onClick={() => fetchResults(page - 1)}
                    >
                      Previous
                    </Button>
                    <Text>
                      Page {page} of {Math.ceil(totalFound / pageSize)}
                    </Text>
                    <Button
                      disabled={page * pageSize >= totalFound}
                      onClick={() => fetchResults(page + 1)}
                    >
                      Next
                    </Button>
                  </Space>
                </div>
              )}
            </>
          )}
        </div>
        </>
        )}

        {/* System Detail Modal */}
        <Modal
          title={
            <Space>
              <SafetyCertificateOutlined style={{ color: '#1890ff' }} />
              System Details - AHRI #{renderAhriIdText(selectedSystem?.ahriId)}
            </Space>
          }
          open={detailModalOpen}
          onCancel={() => setDetailModalOpen(false)}
          width={800}
          footer={[
            <Button key="close" onClick={() => setDetailModalOpen(false)}>
              Close
            </Button>,
            <Button
              key="add"
              type="primary"
              icon={<ShoppingCartOutlined />}
              data-event-context="system_modal_add_to_cart"
              data-ahri-id={String(selectedSystem?.ahriId || '')}
              data-outdoor-product={String(selectedSystem?.outdoorProductCode || '')}
              data-indoor-product={String(selectedSystem?.indoorProductCode || '')}
              data-furnace-product={String(selectedSystem?.furnaceProductCode || '')}
              onClick={() => {
                addToCart(selectedSystem, 'system');
                setDetailModalOpen(false);
              }}
            >
              Add Complete System to Cart
            </Button>,
          ]}
        >
          {selectedSystem && (
            <div>
              {/* Products in System */}
              <Card size="small" className="mb-4" title="System Components">
                {selectedSystem.hasComponentChoices && (
                  <div className="ahri-component-pickers mb-3">
                    {['outdoor', 'indoor', 'furnace'].map((slotType) => {
                      const option = selectedSystem.componentOptions?.[slotType];
                      const codes = option?.codes || [];
                      if (codes.length <= 1) return null;
                      const fieldByType = {
                        outdoor: selectedSystem.outdoorProductCode,
                        indoor: selectedSystem.indoorProductCode,
                        furnace: selectedSystem.furnaceProductCode,
                      };
                      // Hide out-of-stock alternatives when availability is
                      // known (parity with the embeddable widget). Availability
                      // is only populated when product details were fetched with
                      // a selected warehouse; otherwise all options stay visible.
                      const isInStock = (code) => {
                        const av = productDetails[code]?.availability;
                        if (!av) return true;
                        return Boolean(av.in_selected_warehouse);
                      };
                      const { visibleCodes, selectedCode } = resolveSlotSelection(codes, {
                        isInStock,
                        hideOutOfStock: true,
                        previousSelection: fieldByType[slotType],
                      });
                      const optionDiffs = computeOptionDifferentiators(
                        visibleCodes.map((code) => ({
                          code,
                          attributes: productDetails[code]?.attributes,
                          description: decodeHtmlEntities(productDetails[code]?.description),
                          category: productDetails[code]?.category,
                        })),
                        { maxKeys: 3 }
                      );
                      const diffKeyLabels = (optionDiffs.labelsByCode[visibleCodes[0]] || []).map(
                        (entry) => entry.keyLabel
                      );
                      return (
                        <div key={`picker-${slotType}`} className="ahri-component-picker mb-2">
                          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                            {option.label} — {visibleCodes.length} options
                            {diffKeyLabels.length ? ` (differ by ${diffKeyLabels.join(', ')})` : ''}
                          </Text>
                          <AutoComplete
                            style={{ width: '100%' }}
                            value={fieldByType[slotType] || selectedCode}
                            options={visibleCodes.map((code) => {
                              const name = decodeHtmlEntities(productDetails[code]?.name) || code;
                              const av = productDetails[code]?.availability;
                              const stockText = av
                                ? av.in_selected_warehouse
                                  ? ' · In stock'
                                  : ' · Out of stock'
                                : '';
                              const diffText = (optionDiffs.labelsByCode[code] || [])
                                .map((d) => ` · ${d.keyLabel}: ${d.value}`)
                                .join('');
                              return {
                                value: code,
                                label: `${name}${name !== code ? ` (${code})` : ''}${stockText}${diffText}`,
                              };
                            })}
                            onChange={(value) => selectSystemComponent(slotType, value)}
                            filterOption={(input, opt) =>
                              String(opt?.value || '').toLowerCase().includes(String(input || '').toLowerCase())
                            }
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
                <Row gutter={16}>
                  {/* Outdoor Unit */}
                  {(selectedSystem.outdoorProductCode || selectedSystem.outdoorModel) && (
                    <Col xs={24} md={8} className="mb-3">
                      <div className="system-product-wrapper">
                      <div
                        role="button"
                        tabIndex={0}
                        className="system-product-card text-center w-100"
                        onClick={() => toggleProductExpand(selectedSystem.outdoorProductCode)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            toggleProductExpand(selectedSystem.outdoorProductCode);
                          }
                        }}
                      >
                        {renderImage(
                          productDetails[selectedSystem.outdoorProductCode]?.image_url,
                          'Outdoor Unit',
                          100,
                          { borderRadius: 8, marginBottom: 8 }
                        )}
                        <Tag color="blue" style={{ fontSize: 14 }}>Outdoor Unit</Tag>
                        <div className="system-product-info" style={{ marginTop: 8 }}>
                          <Text strong style={{ fontSize: 14, display: 'block', color: '#1a1a1a' }}>
                            {decodeHtmlEntities(productDetails[selectedSystem.outdoorProductCode]?.name) || selectedSystem.outdoorBrand}
                          </Text>
                          <Text style={{ fontSize: 13, color: '#555555' }}>
                            {productDetails[selectedSystem.outdoorProductCode]?.manufacturer_part_number || selectedSystem.outdoorProductCode || selectedSystem.outdoorModel}
                          </Text>
                          {productDetails[selectedSystem.outdoorProductCode]?.category && (
                            <div><Text style={{ fontSize: 13, color: '#555555' }}>{productDetails[selectedSystem.outdoorProductCode].category}</Text></div>
                          )}
                        </div>
                        {selectedSystem.outdoorProductCode && (
                          <Button
                            size="small"
                            icon={<PlusOutlined />}
                            className="system-product-action"
                            data-event-context="component_add_to_cart"
                            data-ahri-id={String(selectedSystem?.ahriId || '')}
                            data-product-code={String(selectedSystem?.outdoorProductCode || '')}
                            data-product-type="outdoor"
                            onClick={(event) => {
                              event.stopPropagation();
                              addToCart({ ...selectedSystem, productCode: selectedSystem.outdoorProductCode, productType: 'outdoor', productName: productDetails[selectedSystem.outdoorProductCode]?.name || selectedSystem.outdoorProductCode }, 'product');
                            }}
                            style={{ marginTop: 8 }}
                          >
                            Add to Cart
                          </Button>
                        )}
                      </div>
                      {selectedSystem.outdoorProductCode && (
                        <div
                          className={`product-expand ${expandedProductCodes[selectedSystem.outdoorProductCode] ? 'is-open' : ''}`}
                        >
                          <div className="product-expand__inner">
                            {productDetails[selectedSystem.outdoorProductCode]?.description && (
                              <div className="product-expand__desc">
                                {productDetails[selectedSystem.outdoorProductCode].description}
                              </div>
                            )}
                            {productDetails[selectedSystem.outdoorProductCode]?.attributes &&
                              Object.keys(productDetails[selectedSystem.outdoorProductCode].attributes || {}).length > 0 && (
                                <div className="product-attributes mt-2">
                                  {Object.entries(productDetails[selectedSystem.outdoorProductCode].attributes || {}).map(
                                    ([key, value]) => (
                                      <div className="product-attributes__row" key={key}>
                                        <span>{key}</span>
                                        <strong>
                                          {Array.isArray(value)
                                            ? value.filter((v) => v !== null && v !== undefined && v !== '').join(', ')
                                            : String(value ?? '')}
                                        </strong>
                                      </div>
                                    )
                                  )}
                                </div>
                              )}
                          </div>
                        </div>
                      )}
                      </div>
                    </Col>
                  )}
                  
                  {/* Indoor Unit */}
                  {(selectedSystem.indoorProductCode || selectedSystem.indoorModel) && (
                    <Col xs={24} md={8} className="mb-3">
                      <div className="system-product-wrapper">
                      <div
                        role="button"
                        tabIndex={0}
                        className="system-product-card text-center w-100"
                        onClick={() => toggleProductExpand(selectedSystem.indoorProductCode)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            toggleProductExpand(selectedSystem.indoorProductCode);
                          }
                        }}
                      >
                        {renderImage(
                          productDetails[selectedSystem.indoorProductCode]?.image_url,
                          'Indoor Unit',
                          100,
                          { borderRadius: 8, marginBottom: 8 }
                        )}
                        <Tag color="green" style={{ fontSize: 14 }}>Indoor Unit</Tag>
                        <div className="system-product-info" style={{ marginTop: 8 }}>
                          <Text strong style={{ fontSize: 14, display: 'block', color: '#1a1a1a' }}>
                            {decodeHtmlEntities(productDetails[selectedSystem.indoorProductCode]?.name) || selectedSystem.indoorBrand}
                          </Text>
                          <Text style={{ fontSize: 13, color: '#555555' }}>
                            {productDetails[selectedSystem.indoorProductCode]?.manufacturer_part_number || selectedSystem.indoorProductCode || selectedSystem.indoorModel}
                          </Text>
                          {productDetails[selectedSystem.indoorProductCode]?.category && (
                            <div><Text style={{ fontSize: 13, color: '#555555' }}>{productDetails[selectedSystem.indoorProductCode].category}</Text></div>
                          )}
                        </div>
                        {selectedSystem.indoorProductCode && (
                          <Button
                            size="small"
                            icon={<PlusOutlined />}
                            className="system-product-action"
                            data-event-context="component_add_to_cart"
                            data-ahri-id={String(selectedSystem?.ahriId || '')}
                            data-product-code={String(selectedSystem?.indoorProductCode || '')}
                            data-product-type="indoor"
                            onClick={(event) => {
                              event.stopPropagation();
                              addToCart({ ...selectedSystem, productCode: selectedSystem.indoorProductCode, productType: 'indoor', productName: productDetails[selectedSystem.indoorProductCode]?.name || selectedSystem.indoorProductCode }, 'product');
                            }}
                            style={{ marginTop: 8 }}
                          >
                            Add to Cart
                          </Button>
                        )}
                      </div>
                      {selectedSystem.indoorProductCode && (
                        <div
                          className={`product-expand ${expandedProductCodes[selectedSystem.indoorProductCode] ? 'is-open' : ''}`}
                        >
                          <div className="product-expand__inner">
                            {productDetails[selectedSystem.indoorProductCode]?.description && (
                              <div className="product-expand__desc">
                                {productDetails[selectedSystem.indoorProductCode].description}
                              </div>
                            )}
                            {productDetails[selectedSystem.indoorProductCode]?.attributes &&
                              Object.keys(productDetails[selectedSystem.indoorProductCode].attributes || {}).length > 0 && (
                                <div className="product-attributes mt-2">
                                  {Object.entries(productDetails[selectedSystem.indoorProductCode].attributes || {}).map(
                                    ([key, value]) => (
                                      <div className="product-attributes__row" key={key}>
                                        <span>{key}</span>
                                        <strong>
                                          {Array.isArray(value)
                                            ? value.filter((v) => v !== null && v !== undefined && v !== '').join(', ')
                                            : String(value ?? '')}
                                        </strong>
                                      </div>
                                    )
                                  )}
                                </div>
                              )}
                          </div>
                        </div>
                      )}
                      </div>
                    </Col>
                  )}
                  
                  {/* Furnace */}
                  {(selectedSystem.furnaceProductCode || selectedSystem.furnaceModel) && (
                    <Col xs={24} md={8} className="mb-3">
                      <div className="system-product-wrapper">
                      <div
                        role="button"
                        tabIndex={0}
                        className="system-product-card text-center w-100"
                        onClick={() => toggleProductExpand(selectedSystem.furnaceProductCode)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            toggleProductExpand(selectedSystem.furnaceProductCode);
                          }
                        }}
                      >
                        {renderImage(
                          productDetails[selectedSystem.furnaceProductCode]?.image_url,
                          'Furnace',
                          100,
                          { borderRadius: 8, marginBottom: 8 }
                        )}
                        <Tag color="orange" style={{ fontSize: 14 }}>Furnace</Tag>
                        <div className="system-product-info" style={{ marginTop: 8 }}>
                          <Text strong style={{ fontSize: 14, display: 'block', color: '#1a1a1a' }}>
                            {decodeHtmlEntities(productDetails[selectedSystem.furnaceProductCode]?.name) || 'Furnace'}
                          </Text>
                          <Text style={{ fontSize: 13, color: '#555555' }}>
                            {productDetails[selectedSystem.furnaceProductCode]?.manufacturer_part_number || selectedSystem.furnaceProductCode || selectedSystem.furnaceModel}
                          </Text>
                          {productDetails[selectedSystem.furnaceProductCode]?.category && (
                            <div><Text style={{ fontSize: 13, color: '#555555' }}>{productDetails[selectedSystem.furnaceProductCode].category}</Text></div>
                          )}
                        </div>
                        {selectedSystem.furnaceProductCode && (
                          <Button
                            size="small"
                            icon={<PlusOutlined />}
                            className="system-product-action"
                            data-event-context="component_add_to_cart"
                            data-ahri-id={String(selectedSystem?.ahriId || '')}
                            data-product-code={String(selectedSystem?.furnaceProductCode || '')}
                            data-product-type="furnace"
                            onClick={(event) => {
                              event.stopPropagation();
                              addToCart({ ...selectedSystem, productCode: selectedSystem.furnaceProductCode, productType: 'furnace', productName: productDetails[selectedSystem.furnaceProductCode]?.name || selectedSystem.furnaceProductCode }, 'product');
                            }}
                            style={{ marginTop: 8 }}
                          >
                            Add to Cart
                          </Button>
                        )}
                      </div>
                      {selectedSystem.furnaceProductCode && (
                        <div
                          className={`product-expand ${expandedProductCodes[selectedSystem.furnaceProductCode] ? 'is-open' : ''}`}
                        >
                          <div className="product-expand__inner">
                            {productDetails[selectedSystem.furnaceProductCode]?.description && (
                              <div className="product-expand__desc">
                                {productDetails[selectedSystem.furnaceProductCode].description}
                              </div>
                            )}
                            {productDetails[selectedSystem.furnaceProductCode]?.attributes &&
                              Object.keys(productDetails[selectedSystem.furnaceProductCode].attributes || {}).length > 0 && (
                                <div className="product-attributes mt-2">
                                  {Object.entries(productDetails[selectedSystem.furnaceProductCode].attributes || {}).map(
                                    ([key, value]) => (
                                      <div className="product-attributes__row" key={key}>
                                        <span>{key}</span>
                                        <strong>
                                          {Array.isArray(value)
                                            ? value.filter((v) => v !== null && v !== undefined && v !== '').join(', ')
                                            : String(value ?? '')}
                                        </strong>
                                      </div>
                                    )
                                  )}
                                </div>
                              )}
                          </div>
                        </div>
                      )}
                      </div>
                    </Col>
                  )}
                </Row>
              </Card>

              {/* Efficiency Ratings */}
              <Card size="small" className="mb-4" title="Efficiency Ratings">
                <Row gutter={16}>
                  <Col span={6}>
                    <div className="text-center">
                      <ThunderboltOutlined style={{ fontSize: 24, color: '#1890ff' }} />
                      <div>
                        <Text type="secondary">SEER2</Text>
                      </div>
                      <Title level={3} style={{ margin: 0, color: '#1890ff' }}>
                        {selectedSystem.seer2 || '-'}
                      </Title>
                    </div>
                  </Col>
                  <Col span={6}>
                    <div className="text-center">
                      <HomeOutlined style={{ fontSize: 24, color: '#52c41a' }} />
                      <div>
                        <Text type="secondary">HSPF2</Text>
                      </div>
                      <Title level={3} style={{ margin: 0, color: '#52c41a' }}>
                        {selectedSystem.hspf2 || '-'}
                      </Title>
                    </div>
                  </Col>
                  <Col span={6}>
                    <div className="text-center">
                      <ThunderboltOutlined style={{ fontSize: 24, color: '#faad14' }} />
                      <div>
                        <Text type="secondary">EER2</Text>
                      </div>
                      <Title level={3} style={{ margin: 0, color: '#faad14' }}>
                        {selectedSystem.eer2 || '-'}
                      </Title>
                    </div>
                  </Col>
                  <Col span={6}>
                    <div className="text-center">
                      <div style={{ fontSize: 24 }}>❄️</div>
                      <div>
                        <Text type="secondary">Cooling</Text>
                      </div>
                      <Title level={4} style={{ margin: 0 }}>
                        {formatNumber(selectedSystem.coolingCapacity)} BTU
                      </Title>
                    </div>
                  </Col>
                </Row>
              </Card>

              {/* Status Badges */}
              <Space wrap className="mb-4">
                {selectedSystem.isEnergyStar && (
                  <Tag color="green" icon={<CheckCircleOutlined />}>
                    Energy Star Certified
                  </Tag>
                )}
                {selectedSystem.completeMatch && (
                  <Tag color="blue" icon={<CheckCircleOutlined />}>
                    Complete Product Match
                  </Tag>
                )}
                {selectedSystem.partialMatch && !selectedSystem.completeMatch && (
                  <Tag color="orange">Partial Product Match</Tag>
                )}
                {selectedSystem.programName && (
                  <Tag>{selectedSystem.programName}</Tag>
                )}
                {selectedSystem.region && (
                  <Tag>{selectedSystem.region}</Tag>
                )}
              </Space>

              {/* Product Attributes */}
              {Object.keys(selectedSystem.matchedProductAttrs || {}).length > 0 && (
                <Card size="small" title="Matched Product Attributes">
                  <Row gutter={[8, 8]}>
                    {Object.entries(selectedSystem.matchedProductAttrs).map(([key, values]) => (
                      <Col span={12} key={key}>
                        <Text type="secondary">{key}:</Text>{' '}
                        <Text>{Array.isArray(values) ? values.join(', ') : values}</Text>
                      </Col>
                    ))}
                  </Row>
                </Card>
              )}

            </div>
          )}
        </Modal>

        {/* Product Detail Drawer */}
        <Drawer
          title={selectedProduct?.name || 'Product Details'}
          placement="right"
          onClose={() => setProductDrawerOpen(false)}
          open={productDrawerOpen}
          width={productDrawerWidth}
          destroyOnClose
        >
          <div
            style={{
              position: 'absolute',
              left: -6,
              top: 0,
              width: 12,
              height: '100%',
              cursor: 'col-resize',
            }}
            onMouseDown={(event) => {
              productDrawerResizeRef.current = { startX: event.clientX, startWidth: productDrawerWidth };
              setProductDrawerResizing(true);
            }}
          />
          {selectedProduct && (
            <div className="product-detail">
              <div className="product-hero">
                <div className="product-hero__media">
                  {renderImage(
                    selectedProduct.data?.image_url,
                    selectedProduct.name,
                    120,
                    { borderRadius: 12 }
                  )}
                </div>
                <div className="product-hero__content">
                  <div className="product-hero__title">{selectedProduct.name}</div>
                  <div className="product-hero__subtitle">
                    {selectedProduct.data?.manufacturer_part_number || selectedProduct.code}
                  </div>
                  <div className="product-hero__meta">
                    {selectedProduct.data?.category && <Tag color="blue">{selectedProduct.data.category}</Tag>}
                    {selectedProduct.type && (
                      <Tag color="geekblue" className="text-capitalize">
                        {selectedProduct.type} unit
                      </Tag>
                    )}
                  </div>
                  <div className="product-hero__actions">
                    <Button
                      type="primary"
                      icon={<ShoppingCartOutlined />}
                      data-event-context="product_drawer_add_to_cart"
                      data-ahri-id={String(selectedProduct?.system?.ahriId || '')}
                      data-product-code={String(selectedProduct?.code || '')}
                      data-product-type={String(selectedProduct?.type || '')}
                      onClick={() =>
                        addToCart(
                          {
                            ...selectedProduct.system,
                            productCode: selectedProduct.code,
                            productType: selectedProduct.type,
                            productName: selectedProduct.name,
                          },
                          'product'
                        )
                      }
                    >
                      Add to Cart
                    </Button>
                    <Button onClick={() => setProductDrawerOpen(false)}>Close</Button>
                  </div>
                </div>
              </div>

              <div className="row g-3 mt-3">
                <div className="col-12 col-lg-6">
                  <div className="product-card">
                    <div className="product-card__title">Highlights</div>
                    <div className="product-card__body">
                      <div className="product-kv">
                        <span>Product Code</span>
                        <strong>{selectedProduct.code}</strong>
                      </div>
                      {selectedProduct.data?.manufacturer_name && (
                        <div className="product-kv">
                          <span>Manufacturer</span>
                          <strong>{selectedProduct.data.manufacturer_name}</strong>
                        </div>
                      )}
                      {selectedProduct.data?.brand_name && (
                        <div className="product-kv">
                          <span>Brand</span>
                          <strong>{selectedProduct.data.brand_name}</strong>
                        </div>
                      )}
                      {selectedProduct.data?.model_number && (
                        <div className="product-kv">
                          <span>Model</span>
                          <strong>{selectedProduct.data.model_number}</strong>
                        </div>
                      )}
                      {selectedProduct.data?.series && (
                        <div className="product-kv">
                          <span>Series</span>
                          <strong>{selectedProduct.data.series}</strong>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="col-12 col-lg-6">
                  <div className="product-card">
                    <div className="product-card__title">Specifications</div>
                    <div className="product-card__body">
                      {[
                        ['SEER2', selectedProduct.data?.seer2 || selectedProduct.data?.seer],
                        ['HSPF2', selectedProduct.data?.hspf2 || selectedProduct.data?.hspf],
                        ['EER2', selectedProduct.data?.eer2 || selectedProduct.data?.eer],
                        ['Tonnage', selectedProduct.data?.tonnage || selectedProduct.data?.capacity_tons],
                        ['Voltage', selectedProduct.data?.voltage],
                        ['Category', selectedProduct.data?.category],
                      ]
                        .filter(([, value]) => value !== undefined && value !== null && value !== '')
                        .map(([label, value]) => (
                          <div className="product-kv" key={label}>
                            <span>{label}</span>
                            <strong>{value}</strong>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              </div>

              {selectedProduct.data?.description && (
                <div className="product-card mt-3">
                  <div className="product-card__title">Description</div>
                  <div className="product-card__body text-muted">{selectedProduct.data.description}</div>
                </div>
              )}

              {selectedProduct.data?.attributes &&
                Object.keys(selectedProduct.data.attributes || {}).length > 0 && (
                  <div className="product-card mt-3">
                    <div className="product-card__title">Product Attributes</div>
                    <div className="product-attributes">
                      {Object.entries(selectedProduct.data.attributes || {}).map(([key, value]) => (
                        <div className="product-attributes__row" key={key}>
                          <span>{key}</span>
                          <strong>
                            {Array.isArray(value)
                              ? value.filter((v) => v !== null && v !== undefined && v !== '').join(', ')
                              : String(value ?? '')}
                          </strong>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </div>
          )}
        </Drawer>

        {/* Cart Drawer */}
        <Drawer
          title={
            <Space>
              <ShoppingCartOutlined />
              Shopping Cart ({cartItemCount} items)
            </Space>
          }
          placement="right"
          onClose={() => setCartDrawerOpen(false)}
          open={cartDrawerOpen}
          width={450}
          footer={
            <div>
              {cart.length > 0 && (
                <>
                  <Divider style={{ margin: '12px 0' }} />
                  <div className="d-flex justify-content-between mb-3">
                    <Text strong>Total Items:</Text>
                    <Text strong>{cartItemCount}</Text>
                  </div>
                  <Button type="primary" block size="large">
                    Proceed to Checkout
                  </Button>
                </>
              )}
            </div>
          }
        >
          {cart.length === 0 ? (
            <Empty description="Your cart is empty" />
          ) : (
            <List
              dataSource={cart}
              renderItem={(item) => (
                <List.Item
                  actions={[
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => removeFromCart(item.id)}
                    />,
                  ]}
                >
                  <List.Item.Meta
                    avatar={
                      <Avatar
                        style={{
                          backgroundColor:
                            item.type === 'system'
                              ? '#1890ff'
                              : item.type === 'outdoor'
                              ? '#52c41a'
                              : item.type === 'indoor'
                              ? '#faad14'
                              : '#722ed1',
                        }}
                        icon={
                          item.type === 'system' ? (
                            <SafetyCertificateOutlined />
                          ) : (
                            <HomeOutlined />
                          )
                        }
                      />
                    }
                    title={
                      <div>
                        <Text strong style={{ fontSize: 15, color: '#1a1a1a' }}>
                          {item.type === 'system'
                            ? `Complete System`
                            : `${item.type.charAt(0).toUpperCase() + item.type.slice(1)} Unit`}
                        </Text>
                        <br />
                        <Text style={{ fontSize: 14, color: '#555555' }}>
                          AHRI #{renderAhriIdText(item.ahriId)}
                        </Text>
                      </div>
                    }
                    description={
                      <div>
                        {item.type === 'system' ? (
                          <>
                            <Text style={{ fontSize: 14, color: '#333333' }}>
                              {item.outdoorBrand} / {item.indoorBrand}
                            </Text>
                          </>
                        ) : (
                          <Text style={{ fontSize: 14, color: '#333333' }}>{item.model}</Text>
                        )}
                        <div className="mt-2">
                          <Space>
                            <Button
                              size="small"
                              icon={<MinusOutlined />}
                              onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            />
                            <InputNumber
                              size="small"
                              min={1}
                              value={item.quantity}
                              onChange={(v) => updateQuantity(item.id, v)}
                              style={{ width: 50 }}
                            />
                            <Button
                              size="small"
                              icon={<PlusOutlined />}
                              onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            />
                          </Space>
                        </div>
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          )}
        </Drawer>
      </div>
      <style>{`
        .ahri-details-btn {
          border-color: #6366f1;
          color: #6366f1;
        }
        .ahri-card-slot-options {
          display: flex;
          flex-direction: column;
          gap: 8px;
          padding: 8px 10px;
          background: #f4f8ff;
          border: 1px dashed #bcd4f5;
          border-radius: 8px;
        }
        .ahri-card-slot-option-group {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .ahri-card-slot-option-chips {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
        }
        .ahri-card-slot-chip {
          display: inline-flex;
          flex-direction: column;
          gap: 3px;
          max-width: 100%;
          padding: 4px 8px;
          font-size: 12px;
          line-height: 1.35;
          color: #475569;
          background: #ffffff;
          border: 1px solid #cdd8e8;
          border-radius: 10px;
        }
        .ahri-card-slot-chip.is-selected {
          color: #0b4a8f;
          background: #e6f1ff;
          border-color: #2f80ed;
          font-weight: 600;
        }
        .ahri-card-slot-chip-name {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 220px;
        }
        .ahri-card-slot-chip-diffs {
          display: flex;
          flex-wrap: wrap;
          gap: 4px;
        }
        .ahri-card-slot-chip-diff {
          display: inline-flex;
          align-items: baseline;
          gap: 3px;
          padding: 1px 6px;
          font-size: 10px;
          line-height: 1.4;
          background: #fff6e6;
          border: 1px solid #ffd591;
          border-radius: 6px;
        }
        .ahri-card-slot-chip.is-selected .ahri-card-slot-chip-diff {
          background: #ffffff;
          border-color: #ffc069;
        }
        .ahri-card-slot-chip-diff-key {
          color: #ad6800;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.02em;
        }
        .ahri-card-slot-chip-diff-val {
          color: #613400;
          font-weight: 600;
        }
        .ahri-card-slot-chip-desc {
          font-size: 10px;
          color: #64748b;
          line-height: 1.35;
          white-space: normal;
        }
        .ahri-details-btn:hover,
        .ahri-details-btn:focus {
          border-color: #4338ca;
          color: #4338ca;
        }
        .product-detail {
          padding-bottom: 24px;
        }
        .product-hero {
          display: grid;
          grid-template-columns: minmax(160px, 220px) 1fr;
          gap: 20px;
          align-items: center;
          background: linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%);
          border-radius: 16px;
          padding: 20px;
          border: 1px solid #e2e8f0;
        }
        .product-hero__media img {
          width: 100%;
          height: 180px;
          object-fit: contain;
          border-radius: 12px;
          background: #ffffff;
        }
        .product-hero__placeholder {
          width: 100%;
          height: 180px;
          border-radius: 12px;
          background: #e2e8f0;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 32px;
          color: #64748b;
        }
        .product-hero__title {
          font-size: 20px;
          font-weight: 700;
          color: #0f172a;
        }
        .product-hero__subtitle {
          color: #475569;
          margin-top: 4px;
        }
        .product-hero__meta {
          margin-top: 12px;
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }
        .product-hero__actions {
          margin-top: 16px;
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }
        .product-card {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 16px;
          box-shadow: 0 6px 16px rgba(15, 23, 42, 0.06);
        }
        .product-card__title {
          font-weight: 600;
          color: #0f172a;
          margin-bottom: 12px;
        }
        .product-kv {
          display: flex;
          justify-content: space-between;
          gap: 12px;
          padding: 6px 0;
          border-bottom: 1px dashed #e2e8f0;
        }
        .product-kv:last-child {
          border-bottom: none;
        }
        .product-kv span {
          color: #64748b;
          font-size: 13px;
        }
        .product-kv strong {
          color: #0f172a;
          font-weight: 600;
        }
        .product-expand {
          max-height: 0;
          opacity: 0;
          overflow: hidden;
          transition: max-height 0.35s ease, opacity 0.35s ease;
          background: #f8fafc;
          border-radius: 12px;
          border: 1px solid #e2e8f0;
          margin-top: 12px;
        }
        .product-expand.is-open {
          max-height: 1200px;
          opacity: 1;
        }
        .product-expand__inner {
          padding: 12px 14px;
        }
        .product-expand__desc {
          margin-top: 8px;
          color: #475569;
          font-size: 13px;
          line-height: 1.4;
        }
        .system-product-card {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          height: 100%;
          cursor: pointer;
          min-height: 260px;
        }
        .system-product-info {
          flex: 1;
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
        }
        .system-product-action {
          margin-top: auto;
          width: 100%;
          justify-content: center;
        }
        .compare-bar {
          border: 1px solid #e2e8f0;
          background: #f8fafc;
        }
        .compare-bar-inline {
          border: 1px solid #e2e8f0;
          background: #f8fafc;
          border-radius: 10px;
          padding: 8px 12px;
          margin-bottom: 12px;
        }
        .ahri-search-inline {
          display: flex;
          flex-direction: column;
          gap: 8px;
          min-width: 260px;
          max-width: 720px;
          width: 100%;
        }
        .filter-stack .ant-col {
          flex: 0 0 100%;
          max-width: 100%;
        }
        @media (min-width: 1200px) {
          .ahri-search-inline {
            flex-direction: row;
            align-items: center;
            gap: 12px;
          }
          .ahri-search-inline .ant-select {
            flex: 1;
          }
        }
        .product-attributes {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .product-attributes__row {
          display: flex;
          justify-content: space-between;
          gap: 12px;
          padding: 8px 0;
          border-bottom: 1px solid #f1f5f9;
        }
        .product-attributes__row:last-child {
          border-bottom: none;
        }
        .product-attributes__row span {
          color: #64748b;
          font-size: 13px;
          text-transform: capitalize;
        }
        .product-attributes__row strong {
          color: #0f172a;
          font-weight: 600;
          text-align: right;
        }
      `}</style>
    </ConfigProvider>
  );
};

export default AhriSystemShop;
